import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import {
    ChevronLeft,
    Eye,
    Download,
    Trash2,
    UserPlus,
    Plus,
    CheckCircle2,
    RefreshCw,
    CloudUpload,
    Loader2,
    XCircle,
    Clock,
    ClipboardList,
    BellRing,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { YourCheckaNavigation } from '@/components/Navbar';
import PreviouslyReportedBanner from '@/components/PreviouslyReportedBanner';
import ValidationModal from '@/components/modals/ValidationModal';
import DeleteInvestigationModal from '@/components/modals/DeleteInvestigationModal';
import RequestInfoModal from '@/components/modals/RequestInfoModal';
import NudgeModal from '@/components/modals/NudgeModal';
import SocialMediaPanel from '@/components/SocialMediaPanel';
import SocialMediaReportPanel from '@/components/SocialMediaReportPanel';
import { validateInvestigationFile } from '@/utils/fileValidation';
import AddressVisitsPanel from '@/components/AddressVisitsPanel';
import InvestigationChecklistPanel from '@/components/InvestigationChecklistPanel';
import MilestonesPanel from '@/components/MilestonesPanel';
import GuarantorsPanel from '@/components/GuarantorsPanel';
import CsChecklistPanel from '@/components/CsChecklistPanel';
import EmployerReferencesPanel from '@/components/EmployerReferencesPanel';
import MilestoneTimeline from '@/components/MilestoneTimeline';
import SlaCountdown from '@/components/SlaCountdown';
import ReportSectionEditor from '@/components/ReportSectionEditor';
import EmployerConversationsPanel from '@/components/EmployerConversationsPanel';
import AgentBriefCard from '@/components/AgentBriefCard';
import {
    parseReportSections,
    serializeReportSections,
    validateReportSections,
    type ReportSection,
} from '@/utils/reportSections';
import {
    useGetInvestigationByIdQuery,
    useDeleteDocumentMutation,
    useGetAllUsersQuery,
    useAssignAgentMutation,
    useReassignInvestigationMutation,
    useAdminAssignCustomerSuccessMutation,
    useAdminAssignMedicalUserMutation,
    useAdminAssignNotaryMutation,
    useAdminAssignPoliceMutation,
    useValidateInvestigationReportMutation,
    useRetriggerConsentFormMutation,
    useSubmitRiskAssessmentMutation,
    useGetRiskAssessmentQuery,
    useGetRiskAssessmentHistoryQuery,
    useGetFilteredDocumentsQuery,
    useUploadAgentDocumentMutation,
    useUploadInvestigationDocumentMutation,
    useApproveDocumentMutation,
    useSubmitReportMutation,
    useUpdateInvestigationReportMutation,
    useGetInvestigationChecklistQuery,
    useGetInvestigationMilestonesQuery,
    useGetAddressVisitsQuery,
} from '@/store/api/apiSlice';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { CheckType, InvestigationStatus, UserRole, RiskAssessmentSubmission, DocumentType } from '@/types';
import { resolveFileUrl } from '@/utils/fileUrl';

type TabType = 'scope' | 'details' | 'report' | 'conversations';
type SubTabType = 'personal' | 'documents';
type EmployerReportSubTab = 'risk-score' | 'field-report' | 'guarantors';

// The detailed report is no longer authored section-by-section — milestones are
// the single source of truth. Each submitted milestone becomes one numbered
// section (its title is the heading, its summary + details the body) so the
// report stays a single compiled document and the backend keeps receiving the
// same `reportDetails` text shape it always has.
function buildReportSectionsFromMilestones(
    milestones?: import('@/types').InvestigationMilestone[] | null,
): ReportSection[] {
    if (!milestones || milestones.length === 0) return [];
    return [...milestones]
        .sort((a, b) => a.order - b.order)
        .map((m) => ({
            heading: m.title,
            body: [m.summary?.trim(), m.details?.trim()].filter(Boolean).join('\n\n'),
        }));
}

function buildReportDetailsFromMilestones(milestones?: import('@/types').InvestigationMilestone[] | null): string {
    return serializeReportSections(buildReportSectionsFromMilestones(milestones));
}

// Safely parse a JSON-string array column (addressHistory / workHistory /
// educationHistory). Returns [] on null/invalid JSON.
function parseJsonArray<T>(raw?: string | null): T[] {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
        return [];
    }
}

// Compact read-only card surfacing the Customer-Success-era fields: category,
// the Medical Analysis add-on, and the assigned CS / medical user. Social handles
// have their own always-visible panel (SocialMediaPanel) so agents and CS can't miss them.
function InvestigationAddonsCard({ investigation }: { investigation: import('@/types').Investigation }) {
    const fullName = (first?: string, last?: string) => `${first ?? ''} ${last ?? ''}`.trim();
    const csName = investigation.customerSuccess
        ? fullName(investigation.customerSuccess.firstName, investigation.customerSuccess.lastName)
        : null;
    const medicalName = investigation.medicalUser
        ? fullName(investigation.medicalUser.firstName, investigation.medicalUser.lastName)
        : null;
    const notaryName = investigation.notaryUser
        ? fullName(investigation.notaryUser.firstName, investigation.notaryUser.lastName)
        : null;
    const policeName = investigation.policeUser
        ? fullName(investigation.policeUser.firstName, investigation.policeUser.lastName)
        : null;

    const hasAnything =
        investigation.investigationCategory ||
        investigation.medicalAnalysisIncluded ||
        csName ||
        medicalName ||
        notaryName ||
        policeName;
    if (!hasAnything) return null;

    return (
        <div className="mt-6 rounded-xl border border-[#e0e0e0] p-4 flex flex-col gap-3">
            <h3 className="text-sm font-bold text-[#1b1b1b]">Category & add-ons</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {investigation.investigationCategory && (
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Category</p>
                        <p className="font-medium text-[#1b1b1b] capitalize">
                            {investigation.investigationCategory.replace('_', ' ')}
                        </p>
                    </div>
                )}
                <div>
                    <p className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Medical Analysis</p>
                    <p className="font-medium text-[#1b1b1b]">
                        {investigation.medicalAnalysisIncluded ? 'Included' : 'Not included'}
                    </p>
                </div>
                {csName && (
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Customer Success</p>
                        <p className="font-medium text-[#1b1b1b]">{csName}</p>
                    </div>
                )}
                {medicalName && (
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Medical user</p>
                        <p className="font-medium text-[#1b1b1b]">{medicalName}</p>
                    </div>
                )}
                {notaryName && (
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Notary</p>
                        <p className="font-medium text-[#1b1b1b]">{notaryName}</p>
                        <p
                            className={`mt-0.5 text-xs font-medium ${
                                investigation.notaryCompletedAt ? 'text-green-600' : 'text-amber-600'
                            }`}
                        >
                            {investigation.notaryCompletedAt
                                ? `Notarised ✓ ${new Date(investigation.notaryCompletedAt).toLocaleDateString()}`
                                : 'Assigned — pending'}
                        </p>
                    </div>
                )}
                {policeName && (
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Police</p>
                        <p className="font-medium text-[#1b1b1b]">{policeName}</p>
                        <p
                            className={`mt-0.5 text-xs font-medium ${
                                investigation.policeCompletedAt ? 'text-green-600' : 'text-amber-600'
                            }`}
                        >
                            {investigation.policeCompletedAt
                                ? `Police check ✓ ${new Date(investigation.policeCompletedAt).toLocaleDateString()}`
                                : 'Assigned — pending'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

// Bottom section tabs for personal details
const DETAILS_SECTION_TABS = [
    'Personal Identification',
    'Criminal & Law Enforcement Disclosure',
    'Financial & Directorship History',
    'Political Exposure',
    'Workplace Conduct',
    'Civil Claims',
    'Media Disclosure',
    'Conflict Of Interest',
    'Data Protection & Consent',
    'Professional Discipline',
    'Guarantors',
] as const;

type PersonalDetailsSection = (typeof DETAILS_SECTION_TABS)[number];

// Every document type an agent can upload against an investigation. The backend
// accepts any valid DocumentType (no type whitelist), and employers see them all
// on the report — so the picker offers the full list.
const AGENT_DOCUMENT_TYPES: { value: string; label: string }[] = [
    { value: 'agent_report', label: 'Agent Report' },
    { value: 'investigation_report', label: 'Investigation Report' },
    { value: 'signed_affidavit', label: 'Signed Affidavit' },
    { value: 'notarized_affidavit', label: 'Notarized Affidavit' },
    { value: 'police_character_certificate', label: 'Police Character Certificate' },
    { value: 'social_media_report', label: 'Social Media Report' },
    { value: 'cv', label: 'CV' },
    { value: 'national_id', label: 'National ID' },
    { value: 'nin', label: 'NIN' },
    { value: 'passport', label: 'Passport' },
    { value: 'letter_of_employment', label: 'Letter of Employment' },
    { value: 'other', label: 'Other' },
];

// Human-readable label for a document type (falls back to a de-underscored title).
const DOCUMENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
    AGENT_DOCUMENT_TYPES.map((t) => [t.value, t.label]),
);
function documentTypeLabel(type?: string | null): string {
    if (!type) return 'Document';
    return DOCUMENT_TYPE_LABELS[String(type)] ?? String(type).replace(/_/g, ' ');
}

// Risk assessment categories with checkbox options
const RISK_ASSESSMENT_CATEGORIES = [
    {
        title: 'Identity Integrity',
        category: 'identity_integrity',
        weight: 2,
        options: [
            { label: 'Fully verified, consistent', score: 0 },
            { label: 'Minor inconsistencies', score: 3 },
            { label: 'Identity manipulation or fraud indicators', score: 5 },
        ],
    },
    {
        title: 'Criminal & Anti-Corruption',
        category: 'criminal_anti_corruption',
        weight: 4,
        options: [
            { label: 'No criminal record', score: 0 },
            { label: 'Minor non-financial offence (historical)', score: 2 },
            { label: 'Past financial misconduct resolved', score: 3 },
            { label: 'Ongoing investigation', score: 4 },
            { label: 'Active prosecution or conviction', score: 5 },
        ],
    },
    {
        title: 'Financial & Directorship',
        category: 'financial_directorship',
        weight: 3,
        options: [
            { label: 'Clean record', score: 0 },
            { label: 'Company dissolved (legitimate)', score: 2 },
            { label: 'Multiple failed entities', score: 3 },
            { label: 'Undisclosed beneficial ownership', score: 4 },
            { label: 'Director disqualification/bankruptcy fraud', score: 5 },
        ],
    },
    {
        title: 'Regulatory & Discipline',
        category: 'regulatory_discipline',
        weight: 3,
        options: [
            { label: 'No disciplinary findings', score: 0 },
            { label: 'Past regulatory reprimand', score: 3 },
            { label: 'Licence revoked or severe sanction', score: 5 },
        ],
    },
    {
        title: 'Political Exposure',
        category: 'political_exposure',
        weight: 1,
        options: [
            { label: 'No PEP exposure', score: 0 },
            { label: 'Indirect association', score: 2 },
            { label: 'Declared PEP, no issues', score: 3 },
            { label: 'Undisclosed PEP', score: 4 },
            { label: 'Procurement conflict or corruption', score: 5 },
        ],
    },
    {
        title: 'Litigation History',
        category: 'litigation_history',
        weight: 2,
        options: [
            { label: 'No litigation', score: 0 },
            { label: 'Minor civil disputes', score: 2 },
            { label: 'Repeated fiduciary disputes', score: 3 },
            { label: 'Fraud-based civil judgments', score: 5 },
        ],
    },
    {
        title: 'Safeguarding & Conduct',
        category: 'safeguarding_conduct',
        weight: 2,
        options: [
            { label: 'Clean references', score: 0 },
            { label: 'Allegations without finding', score: 3 },
            { label: 'Confirmed misconduct involving vulnerable persons', score: 5 },
        ],
    },
    {
        title: 'Reputational & Media',
        category: 'reputational_media',
        weight: 3,
        options: [
            { label: 'No adverse media', score: 0 },
            { label: 'Isolated minor controversy', score: 2 },
            { label: 'Repeated negative coverage', score: 4 },
            { label: 'Major corruption scandal', score: 5 },
        ],
    },
    {
        title: 'Conflict of Interest',
        category: 'conflict_of_interest',
        weight: 2,
        options: [
            { label: 'No conflict', score: 0 },
            { label: 'Manageable disclosed conflict', score: 3 },
            { label: 'Undisclosed material conflict', score: 5 },
        ],
    },
    {
        title: 'Guarantor Reliability',
        category: 'guarantor_reliability',
        weight: 1,
        options: [
            { label: 'Verified and credible', score: 0 },
            { label: 'Weak financial standing', score: 3 },
            { label: 'False or unverifiable guarantor', score: 5 },
        ],
    },
] as const;

export default function InvestigationDetails() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    // Default landing tab is Scope of Work for every role — it gives everyone
    // (employer, agent, admin) an orientation of what this investigation covers,
    // and for employers it doubles as the live milestone tracker.
    const [activeTab, setActiveTab] = useState<TabType>('scope');
    const [activeSubTab, setActiveSubTab] = useState<SubTabType>('personal');
    const [reportSubTab, setReportSubTab] = useState<
        'risk-assessment' | 'field-report' | 'visits' | 'milestones' | 'checklist' | 'guarantors'
    >('risk-assessment');
    const [missingChecklistItems, setMissingChecklistItems] = useState<import('@/types').MissingChecklistItem[]>([]);
    const [missingMilestones, setMissingMilestones] = useState<import('@/types').MissingMilestoneRef[]>([]);
    const [reportTitle, setReportTitle] = useState('');
    const [reportExecutiveSummary, setReportExecutiveSummary] = useState('');
    const [reportSections, setReportSections] = useState<ReportSection[]>([]);
    const [reportSectionsError, setReportSectionsError] = useState<{ index: number; message: string } | null>(null);
    const [reportSaveError, setReportSaveError] = useState('');
    const [reportSaveSuccess, setReportSaveSuccess] = useState('');
    const [adminEditMode, setAdminEditMode] = useState(false);
    // When the employer clicks "Message employee", we jump to the Conversations
    // tab AND open the new-conversation composer in one motion.
    const [conversationsStartCompose, setConversationsStartCompose] = useState(false);
    const [supportCodeCopied, setSupportCodeCopied] = useState(false);
    const [showUploadArea, setShowUploadArea] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState<string>('');
    const [showAssignCsModal, setShowAssignCsModal] = useState(false);
    const [selectedCsId, setSelectedCsId] = useState<string>('');
    const [showAssignMedicalModal, setShowAssignMedicalModal] = useState(false);
    const [selectedMedicalId, setSelectedMedicalId] = useState<string>('');
    const [showAssignNotaryModal, setShowAssignNotaryModal] = useState(false);
    const [selectedNotaryId, setSelectedNotaryId] = useState<string>('');
    const [showAssignPoliceModal, setShowAssignPoliceModal] = useState(false);
    const [selectedPoliceId, setSelectedPoliceId] = useState<string>('');
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [personalDetailsSection, setPersonalDetailsSection] =
        useState<PersonalDetailsSection>('Personal Identification');
    const [riskChecked, setRiskChecked] = useState<Record<string, boolean>>({});
    const [riskRationale, setRiskRationale] = useState<Record<string, string>>({});
    const [isDragging, setIsDragging] = useState(false);
    const [employerReportSubTab, setEmployerReportSubTab] = useState<EmployerReportSubTab>('risk-score');
    const [uploadDocType, setUploadDocType] = useState<string>('agent_report');
    const [selectedHistoryVersion, setSelectedHistoryVersion] = useState<number | null>(null);
    const [validationNotes, setValidationNotes] = useState('');
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showEmployerModal, setShowEmployerModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRequestInfoModal, setShowRequestInfoModal] = useState(false);
    const [showNudgeModal, setShowNudgeModal] = useState(false);
    const [nudgeToast, setNudgeToast] = useState('');
    const [showRetriggerModal, setShowRetriggerModal] = useState(false);
    const [retriggerError, setRetriggerError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const currentUser = useAppSelector(selectCurrentUser);
    const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
    const isAgent = currentUser?.role === UserRole.AGENT;
    const isEmployer = currentUser?.role === UserRole.EMPLOYER;
    const isCustomerSuccess = currentUser?.role === UserRole.CUSTOMER_SUCCESS;
    // Super Admin and Customer Success can chase down missing info and nudge parties.
    const canRequestInfo = isSuperAdmin || isCustomerSuccess;
    // Both Super Admin and Customer Success can edit a submitted agent report
    // (title, summary, findings) before it's shared with the employer. They use
    // the same partial-update endpoint and bypass the agent-side workflow gates.
    const isReportEditor = isSuperAdmin || isCustomerSuccess;

    // Get user initials
    const getInitials = (firstName?: string, lastName?: string) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return (first + last).toUpperCase() || 'U';
    };

    // Fetch investigation data from API
    const {
        data: investigation,
        isLoading,
        error,
    } = useGetInvestigationByIdQuery(id || '', {
        skip: !id,
    });

    // Fetch available agents / CS / medical users for assignment dropdowns
    const { data: availableAgents = [] } = useGetAllUsersQuery({ role: 'agent' }, { skip: !isSuperAdmin });
    const { data: availableCs = [] } = useGetAllUsersQuery({ role: 'customer_success' }, { skip: !isSuperAdmin });
    const { data: availableMedical = [] } = useGetAllUsersQuery({ role: 'medical' }, { skip: !isSuperAdmin });
    const { data: availableNotaries = [] } = useGetAllUsersQuery({ role: 'notary' }, { skip: !isSuperAdmin });
    const { data: availablePolice = [] } = useGetAllUsersQuery({ role: 'police' }, { skip: !isSuperAdmin });

    // Delete document mutation
    const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();

    // Agent assignment mutations
    const [assignAgent, { isLoading: isAssigning }] = useAssignAgentMutation();
    const [reassignAgent, { isLoading: isReassigning }] = useReassignInvestigationMutation();

    // CS / Medical / Notary owner assignment mutations (admin)
    const [assignCs, { isLoading: isAssigningCs }] = useAdminAssignCustomerSuccessMutation();
    const [assignMedical, { isLoading: isAssigningMedical }] = useAdminAssignMedicalUserMutation();
    const [assignNotary, { isLoading: isAssigningNotary }] = useAdminAssignNotaryMutation();
    const [assignPolice, { isLoading: isAssigningPolice }] = useAdminAssignPoliceMutation();
    const [validateReport, { isLoading: isValidating }] = useValidateInvestigationReportMutation();
    const [retriggerConsent, { isLoading: isRetriggering }] = useRetriggerConsentFormMutation();

    // Risk assessment queries & mutations (5a)
    const { data: existingRiskAssessment } = useGetRiskAssessmentQuery(id || '', { skip: !id });
    const { data: riskHistory } = useGetRiskAssessmentHistoryQuery(id || '', { skip: !id });
    const [submitRiskAssessment, { isLoading: isSubmittingRisk }] = useSubmitRiskAssessmentMutation();

    // Field report submission (5b). Agents submit via POST submit-report;
    // super-admins editing an already-submitted report use the PATCH route,
    // which is SLA-safe and doesn't touch status / deadlines / pin.
    const [submitReport, { isLoading: isSavingReport }] = useSubmitReportMutation();
    const [updateAdminReport, { isLoading: isUpdatingAdminReport }] = useUpdateInvestigationReportMutation();

    // Checklist — used to gate Field Report saves (must be complete first)
    const { data: investigationChecklist } = useGetInvestigationChecklistQuery(id || '', { skip: !id });
    const isChecklistComplete = investigationChecklist?.isComplete === true;

    // Milestones — server gates the final submit on every milestone being submitted.
    // We use the same data on both agent (full content) and employer/admin
    // (progress-only) views — the server strips fields per role.
    const { data: milestonesData } = useGetInvestigationMilestonesQuery(id || '', { skip: !id });
    const allMilestonesSubmitted = milestonesData?.allSubmitted === true;
    const hasMilestones = (milestonesData?.totalMilestones ?? 0) > 0;

    // Address visits — at least one must exist before the agent can save the report
    const { data: agentVisits } = useGetAddressVisitsQuery(id || '', { skip: !id });
    const hasAtLeastOneVisit = (agentVisits?.length ?? 0) > 0;
    // Risk assessment must be submitted too — the server stamps
    // lastRiskAssessmentAt on submit, which is the canonical "yes this happened"
    // signal regardless of which view loaded the side-channel assessment query.
    const hasRiskAssessment = !!investigation?.lastRiskAssessmentAt;
    // Final submit is also gated on every milestone being submitted (server-enforced).
    // If the milestones endpoint hasn't loaded yet, we let the legacy gates carry
    // the decision — the server will still reject if anything is outstanding.
    const milestonesGate = !hasMilestones || allMilestonesSubmitted;
    const canSubmitReport = isChecklistComplete && hasAtLeastOneVisit && hasRiskAssessment && milestonesGate;

    // Filtered documents queries (5c)
    const { data: employeeDocs } = useGetFilteredDocumentsQuery(
        { id: id || '', uploadedBy: 'employee' },
        { skip: !id },
    );
    const { data: agentDocs } = useGetFilteredDocumentsQuery({ id: id || '', uploadedBy: 'agent' }, { skip: !id });
    const { data: allDocs } = useGetFilteredDocumentsQuery({ id: id || '' }, { skip: !id || !isSuperAdmin });

    // Agent document upload mutation (5d)
    const [uploadAgentDoc, { isLoading: isUploading }] = useUploadAgentDocumentMutation();
    // Staff (Super Admin / Customer Success) upload + overwrite via the general
    // documents endpoint. Agents use the agent-documents endpoint above.
    const [uploadStaffDoc, { isLoading: isUploadingStaff }] = useUploadInvestigationDocumentMutation();
    const replaceInputRef = useRef<HTMLInputElement>(null);
    const [replaceTargetType, setReplaceTargetType] = useState<string | null>(null);
    const canManageDocs = isSuperAdmin || isCustomerSuccess;

    // Admin document approval mutation (5e)
    const [approveDoc, { isLoading: isApproving }] = useApproveDocumentMutation();

    // Pre-populate risk checkboxes & rationale from existing assessment
    useEffect(() => {
        if (existingRiskAssessment?.categoryScores) {
            const checked: Record<string, boolean> = {};
            const rationale: Record<string, string> = {};
            existingRiskAssessment.categoryScores.forEach((cs) => {
                checked[`${cs.category}::${cs.selectedCriteria}`] = true;
                if (cs.rationale) rationale[cs.category] = cs.rationale;
            });
            setRiskChecked(checked);
            setRiskRationale(rationale);
        }
    }, [existingRiskAssessment]);

    // Hydrate the field-report form once the investigation loads / refreshes.
    // The detailed-report body is stored as a plain string on the backend but
    // edited here as a structured list of sections — we parse on load and
    // serialize on save so both old free-form reports and new numbered ones
    // round-trip cleanly.
    useEffect(() => {
        if (!investigation) return;
        setReportTitle(investigation.reportTitle ?? '');
        setReportExecutiveSummary(investigation.executiveSummary ?? '');
        const rawDetails = investigation.reportDetails ?? investigation.investigationNotes ?? '';
        setReportSections(parseReportSections(rawDetails));
    }, [investigation?.id, investigation?.reportTitle, investigation?.executiveSummary, investigation?.reportDetails]);

    const handleSaveFieldReport = async () => {
        if (!id) return;
        setReportSaveError('');
        setReportSaveSuccess('');
        setReportSectionsError(null);
        setMissingMilestones([]);

        // Reviewer edits bypass the agent-side gates (checklist + address visit).
        // Those are workflow guardrails for agents writing the original report —
        // they shouldn't block a reviewer (Super Admin / Customer Success) who's
        // correcting a typo or rewording a finding.
        if (!isReportEditor) {
            if (!isChecklistComplete) {
                setReportSaveError('Complete the investigator checklist before saving the field report.');
                setReportSubTab('checklist');
                return;
            }

            if (!hasAtLeastOneVisit) {
                setReportSaveError('Log at least one address visit before saving the field report.');
                setReportSubTab('visits');
                return;
            }

            if (!hasRiskAssessment) {
                setReportSaveError('Submit the risk assessment before saving the field report.');
                setReportSubTab('risk-assessment');
                return;
            }

            if (hasMilestones && !allMilestonesSubmitted) {
                setReportSaveError('Submit every milestone before sending the final report for review.');
                setReportSubTab('milestones');
                return;
            }
        }

        const trimmedTitle = reportTitle.trim();
        if (!trimmedTitle) {
            setReportSaveError('Report Title is required');
            return;
        }
        if (trimmedTitle.length > 500) {
            setReportSaveError('Report Title must be 500 characters or fewer');
            return;
        }

        // Agents no longer write the detailed report by hand — it's compiled from
        // their submitted milestones. Reviewers editing a submitted report still
        // work against the parsed section list so they can tidy wording.
        let reportDetails: string;
        if (isReportEditor) {
            const sectionsError = validateReportSections(reportSections);
            if (sectionsError) {
                setReportSectionsError(sectionsError);
                setReportSaveError(sectionsError.message);
                return;
            }
            reportDetails = serializeReportSections(reportSections);
        } else {
            reportDetails = buildReportDetailsFromMilestones(milestonesData?.milestones);
        }

        try {
            const trimmedSummary = reportExecutiveSummary.trim();
            const body = {
                reportTitle: trimmedTitle,
                ...(trimmedSummary ? { executiveSummary: trimmedSummary } : {}),
                reportDetails,
            };
            // Reviewer edits go through the partial-update endpoint so they don't
            // re-trigger the submit lifecycle (status flip, deadlines, pin issuance).
            const result = isReportEditor
                ? await updateAdminReport({ id, data: body })
                : await submitReport({ id, data: body });
            if ('error' in result) {
                const errData = result.error as any;
                // Server can return either { message: string, ... } at the root or the
                // structured shape nested under `data.message`. Probe both.
                const data = errData?.data;
                const nested = data?.message;
                // Structured 400: { message, missingItems[] } when the checklist is incomplete.
                if (nested && typeof nested === 'object' && Array.isArray(nested.missingItems)) {
                    setMissingChecklistItems(nested.missingItems);
                    setReportSubTab('checklist');
                    setReportSaveError(
                        nested.message || 'Investigator checklist is incomplete. Answer every item before submitting.',
                    );
                    return;
                }
                // Structured 400: { message, missingMilestones[] } when not every milestone has been submitted.
                if (data && Array.isArray(data.missingMilestones)) {
                    setMissingMilestones(data.missingMilestones);
                    setReportSubTab('milestones');
                    setReportSaveError(
                        data.message ||
                            'Every milestone must be submitted before the final report can be sent for review.',
                    );
                    return;
                }
                // Only ever store a string in reportSaveError — a NestJS error body
                // can nest an object under `message` ({ message, error, statusCode }),
                // and rendering that object as a React child crashes the page (#31).
                const asString = (v: unknown): string | undefined =>
                    typeof v === 'string' && v.trim() ? v : undefined;
                setReportSaveError(
                    asString(nested) ||
                        asString(data?.message) ||
                        asString(data?.error) ||
                        asString(errData?.message) ||
                        'Failed to save report. Please try again.',
                );
                return;
            }
            setMissingChecklistItems([]);
            setMissingMilestones([]);
            setReportSaveSuccess(isReportEditor ? 'Report updated.' : 'Report saved. Status changed to Under Review.');
            if (isReportEditor) setAdminEditMode(false);
        } catch (err: any) {
            setReportSaveError(err?.message || 'Failed to save report. Please try again.');
        }
    };

    // Format helper functions
    const formatDate = (dateString?: string) => {
        if (!dateString) return '---';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const calculateAge = (dateOfBirth?: string) => {
        if (!dateOfBirth) return '';
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const getStatusLabel = (status: InvestigationStatus) => {
        const statusLabels: Record<InvestigationStatus, string> = {
            [InvestigationStatus.NEW]: 'New',
            [InvestigationStatus.PENDING]: 'Pending',
            [InvestigationStatus.AWAITING_APPROVAL]: 'Awaiting Approval',
            [InvestigationStatus.IN_PROGRESS]: 'In Progress',
            [InvestigationStatus.UNDER_REVIEW]: 'Under Review',
            [InvestigationStatus.COMPLETED]: 'Completed',
            [InvestigationStatus.CANCELLED]: 'Cancelled',
        };
        return statusLabels[status] || status;
    };

    const getStatusColor = (status: InvestigationStatus) => {
        switch (status) {
            case InvestigationStatus.COMPLETED:
                return 'bg-green-50 text-green-700 border-green-100';
            case InvestigationStatus.IN_PROGRESS:
                return 'bg-blue-50 text-blue-700 border-blue-100';
            case InvestigationStatus.UNDER_REVIEW:
                return 'bg-orange-50 text-orange-700 border-orange-100';
            case InvestigationStatus.PENDING:
            case InvestigationStatus.AWAITING_APPROVAL:
                return 'bg-yellow-50 text-yellow-700 border-yellow-100';
            case InvestigationStatus.NEW:
                return 'bg-purple-50 text-purple-700 border-purple-100';
            case InvestigationStatus.CANCELLED:
                return 'bg-red-50 text-red-700 border-red-100';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-100';
        }
    };

    const getDocStatusBadge = (docStatus?: string) => {
        const status = docStatus || (investigation ? getStatusLabel(investigation.status) : '');
        const lower = status.toLowerCase();
        if (lower.includes('completed') || lower.includes('approved')) {
            return { label: 'Completed', className: 'bg-green-50 text-green-700 border-green-200' };
        }
        if (lower.includes('rejected') || lower.includes('cancelled')) {
            return { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' };
        }
        return { label: 'Awaiting Approval', className: 'bg-orange-50 text-orange-700 border-orange-200' };
    };

    if (isLoading) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading investigation details...</p>
                </div>
            </main>
        );
    }

    if (error || !investigation) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Failed to load investigation details</p>
                    <button
                        onClick={() => navigate('/investigations')}
                        className="px-6 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800"
                    >
                        Back to Investigations
                    </button>
                </div>
            </main>
        );
    }

    const employeeName = `${investigation.employeeFirstName} ${investigation.employeeMiddleName ? investigation.employeeMiddleName + ' ' : ''}${investigation.employeeLastName}`;
    const age = investigation.dateOfBirth ? calculateAge(investigation.dateOfBirth) : null;

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        const file = files?.[0];
        if (!file || !id) return;
        const validationError = validateInvestigationFile(file);
        if (validationError) {
            alert(validationError);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        try {
            // Agents upload through the agent-documents endpoint (approval queue);
            // Super Admin / CS upload through the general documents endpoint.
            if (isAgent) {
                await uploadAgentDoc({ id, file, documentType: uploadDocType }).unwrap();
            } else {
                await uploadStaffDoc({ id, file, documentType: uploadDocType as DocumentType }).unwrap();
            }
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err: any) {
            alert(err?.data?.message || 'Failed to upload document');
        }
    };

    // Replace/overwrite a specific document: re-upload with the same documentType
    // (Super Admin / CS only). Backend replaces the file for that type.
    const openReplaceDocument = (documentType: string) => {
        setReplaceTargetType(documentType || 'other');
        replaceInputRef.current?.click();
    };

    const handleReplaceFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !id || !replaceTargetType) return;
        const validationError = validateInvestigationFile(file);
        if (validationError) {
            alert(validationError);
            return;
        }
        try {
            await uploadStaffDoc({ id, file, documentType: replaceTargetType as DocumentType }).unwrap();
        } catch (err: any) {
            alert(err?.data?.message || 'Failed to replace document');
        } finally {
            setReplaceTargetType(null);
        }
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        const file = files?.[0];
        if (!file || !id) return;
        const validationError = validateInvestigationFile(file);
        if (validationError) {
            alert(validationError);
            return;
        }
        try {
            await uploadAgentDoc({ id, file, documentType: uploadDocType }).unwrap();
        } catch (err: any) {
            alert(err?.data?.message || 'Failed to upload document');
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleViewDocument = (doc: any) => {
        try {
            const fileUrl = doc.downloadUrl || doc.fileUrl;
            if (!fileUrl) {
                alert('Document URL not available');
                return;
            }
            window.open(resolveFileUrl(fileUrl), '_blank');
        } catch (error) {
            console.error('Error viewing document:', error);
            alert('Failed to view document');
        }
    };

    const handleDownloadDocument = async (documentId: string) => {
        if (!id) return;
        const doc = investigation?.documents?.find((d) => d.id === documentId);
        if (!doc) {
            alert('Document not found');
            return;
        }

        const fileUrl = doc.downloadUrl || doc.fileUrl;
        if (!fileUrl) {
            alert('Document URL not available');
            return;
        }
        const absoluteUrl = resolveFileUrl(fileUrl);
        const fileName = doc.fileName || `document-${documentId}`;

        try {
            // Files under /uploads/* are served publicly (the <img> preview loads them
            // with no auth). Do NOT send an Authorization header: it makes the request
            // non-simple, forcing a CORS preflight the static host doesn't answer, which
            // is what was failing here. A plain GET is a simple request — no preflight.
            const response = await fetch(absoluteUrl, { method: 'GET' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            // Revoke on a later tick — revoking synchronously right after click()
            // cancels the download before the browser has read the blob.
            setTimeout(() => {
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }, 1000);
        } catch (error) {
            // If the cross-origin blob read is still blocked, fall back to opening the
            // file directly so the user can save it manually (loses the nice filename).
            console.error('Error downloading document:', error);
            const opened = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
            if (!opened) {
                alert('Failed to download document. Please try again.');
            }
        }
    };

    const handleDeleteDocument = async (documentId: string) => {
        if (!id || !window.confirm('Are you sure you want to delete this document?')) return;

        try {
            await deleteDocument({ investigationId: id, documentId }).unwrap();
            alert('Document deleted successfully');
        } catch (error: any) {
            console.error('Error deleting document:', error);
            alert(parseErrorMessage(error, 'Failed to delete document'));
        }
    };

    const handleRetriggerConsent = async () => {
        if (!id) return;
        setRetriggerError('');
        try {
            const result = await retriggerConsent(id).unwrap();
            setShowRetriggerModal(false);
            const when = result?.dateRequested ? formatDate(result.dateRequested) : '';
            setNudgeToast(
                when
                    ? `New consent link sent. Reminder clock restarted on ${when}. The old link no longer works.`
                    : 'New consent link sent. The old link no longer works.',
            );
        } catch (error: any) {
            setRetriggerError(parseErrorMessage(error, 'Failed to retrigger the consent form.'));
        }
    };

    const handleAssignAgent = async () => {
        if (!id || !selectedAgentId) return;

        try {
            if (investigation?.agentId) {
                await reassignAgent({
                    id,
                    data: { agentId: selectedAgentId },
                }).unwrap();
                alert('Agent reassigned successfully');
            } else {
                await assignAgent({ id, agentId: selectedAgentId }).unwrap();
                alert('Agent assigned successfully');
            }
            setShowAssignModal(false);
            setSelectedAgentId('');
        } catch (error: any) {
            console.error('Error assigning agent:', error);
            alert(parseErrorMessage(error, 'Failed to assign agent'));
        }
    };

    const handleAssignCs = async () => {
        if (!id || !selectedCsId) return;
        try {
            await assignCs({ id, customerSuccessId: selectedCsId }).unwrap();
            alert('Customer Success owner updated');
            setShowAssignCsModal(false);
            setSelectedCsId('');
        } catch (error: any) {
            console.error('Error assigning Customer Success:', error);
            alert(parseErrorMessage(error, 'Failed to assign Customer Success'));
        }
    };

    const handleAssignMedical = async () => {
        if (!id || !selectedMedicalId) return;
        try {
            await assignMedical({ id, medicalUserId: selectedMedicalId }).unwrap();
            alert('Medical user updated');
            setShowAssignMedicalModal(false);
            setSelectedMedicalId('');
        } catch (error: any) {
            console.error('Error assigning medical user:', error);
            alert(parseErrorMessage(error, 'Failed to assign medical user'));
        }
    };

    const handleAssignNotary = async () => {
        if (!id || !selectedNotaryId) return;
        try {
            await assignNotary({ id, notaryUserId: selectedNotaryId }).unwrap();
            alert('Notary updated');
            setShowAssignNotaryModal(false);
            setSelectedNotaryId('');
        } catch (error: any) {
            console.error('Error assigning notary:', error);
            alert(parseErrorMessage(error, 'Failed to assign notary'));
        }
    };

    const handleAssignPolice = async () => {
        if (!id || !selectedPoliceId) return;
        try {
            await assignPolice({ id, policeUserId: selectedPoliceId }).unwrap();
            alert('Police user updated');
            setShowAssignPoliceModal(false);
            setSelectedPoliceId('');
        } catch (error: any) {
            console.error('Error assigning police:', error);
            alert(parseErrorMessage(error, 'Failed to assign police user'));
        }
    };

    const handleValidationSubmit = async (approved: boolean, validationNotes?: string) => {
        if (!id) return;

        try {
            await validateReport({
                id,
                approved,
                ...(validationNotes && { validationNotes }),
            }).unwrap();

            setShowValidationModal(false);

            const message = approved
                ? 'Investigation approved successfully! Earning created.'
                : 'Investigation rejected. Agent will be notified to revise.';
            alert(message);
        } catch (error: any) {
            console.error('Failed to validate investigation:', error);
            alert(parseErrorMessage(error, 'Failed to validate investigation'));
        }
    };

    const handleSubmitRiskAssessment = async () => {
        if (!id) return;

        // Build one score per category from checked options
        const categoryScores = RISK_ASSESSMENT_CATEGORIES.map((cat) => {
            // Find the checked option for this category
            const selectedOption = cat.options.find((opt) => riskChecked[`${cat.category}::${opt.label}`]);
            return {
                category: cat.category,
                rawScore: selectedOption?.score ?? 0,
                selectedCriteria: selectedOption?.label ?? cat.options[0].label,
                rationale: riskRationale[cat.category] || '',
                evidenceReferences: '',
            };
        });

        try {
            await submitRiskAssessment({ id, data: { categoryScores, investigationNotes: '' } }).unwrap();
            alert('Risk assessment submitted successfully');
        } catch (err: any) {
            alert(err?.data?.message || 'Failed to submit risk assessment');
        }
    };

    // ---------- Read-only field component ----------
    const ReadOnlyField = ({ label, value }: { label: string; value: React.ReactNode }) => (
        <div>
            <p className="text-sm font-semibold text-[#1b1b1b] mb-2">{label}</p>
            <div className="border-l-[3px] border-[#d9d9d9] pl-4">
                <p className="text-sm font-bold text-[#1b1b1b]">{value || '---'}</p>
            </div>
        </div>
    );

    // ---------- Document card component ----------
    const DocumentCard = ({ doc, showStatusBadge = false }: { doc: any; showStatusBadge?: boolean }) => {
        const isPDF = doc.mimeType === 'application/pdf';
        const isImage = doc.mimeType?.startsWith('image/');
        const badge = getDocStatusBadge((doc as any).status);

        return (
            <div className="border border-[#e0e0e0] rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
                {/* File preview area */}
                <div className="w-full aspect-[4/3] bg-[#f5f5f5] flex items-center justify-center overflow-hidden relative group">
                    {isImage ? (
                        <img
                            src={resolveFileUrl(doc.fileUrl)}
                            alt={doc.fileName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                            }}
                        />
                    ) : isPDF ? (
                        <div className="flex flex-col items-center gap-1">
                            <div className="w-12 h-14 bg-red-100 rounded flex items-center justify-center">
                                <span className="text-red-600 text-[10px] font-bold">PDF</span>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1.5 px-6 w-full">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className="h-1.5 bg-[#ddd] rounded-full"
                                    style={{ width: `${70 + Math.random() * 30}%` }}
                                />
                            ))}
                        </div>
                    )}
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                            onClick={() => handleViewDocument(doc)}
                            className="text-white hover:scale-110 transition-transform"
                            title="View document"
                        >
                            <Eye className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Title + actions */}
                <div className="px-3 py-2.5">
                    <span className="mb-1.5 inline-block rounded-md bg-[#FF6B35]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#FF6B35]">
                        {documentTypeLabel(doc.documentType)}
                    </span>
                    <p className="text-xs font-medium text-[#1b1b1b] truncate mb-2" title={doc.fileName}>
                        {doc.fileName}
                    </p>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handleViewDocument(doc)}
                            className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
                            title="View"
                        >
                            <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => handleDownloadDocument(doc.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
                            title="Download"
                        >
                            <Download className="w-3.5 h-3.5" />
                        </button>
                        {canManageDocs && (
                            <button
                                onClick={() => openReplaceDocument(doc.documentType)}
                                disabled={isUploadingStaff}
                                className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#FF6B35] transition-colors disabled:opacity-50"
                                title="Replace / overwrite"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            disabled={isDeleting}
                            className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Delete"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                    {showStatusBadge && (
                        <div className="mt-2">
                            <Badge
                                variant="outline"
                                className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${badge.className}`}
                            >
                                {badge.label}
                            </Badge>
                        </div>
                    )}
                    {doc.approvalStatus && (
                        <span
                            className={`inline-block px-2 py-0.5 text-xs rounded-full mt-1 ${
                                doc.approvalStatus === 'approved' || doc.approvalStatus === 'completed'
                                    ? 'bg-green-100 text-green-700'
                                    : doc.approvalStatus === 'rejected'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-orange-100 text-orange-700'
                            }`}
                        >
                            {doc.approvalStatus === 'awaiting_approval'
                                ? 'Awaiting Approval'
                                : doc.approvalStatus.charAt(0).toUpperCase() + doc.approvalStatus.slice(1)}
                        </span>
                    )}
                    {isSuperAdmin && doc.approvalStatus === 'awaiting_approval' && (
                        <div className="flex gap-2 mt-2">
                            <button
                                onClick={async () => {
                                    if (!id) return;
                                    try {
                                        await approveDoc({
                                            investigationId: id,
                                            documentId: doc.id,
                                            data: { status: 'approved' },
                                        }).unwrap();
                                    } catch (err: any) {
                                        alert(err?.data?.message || 'Failed to approve');
                                    }
                                }}
                                disabled={isApproving}
                                className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                            >
                                Approve
                            </button>
                            <button
                                onClick={async () => {
                                    if (!id) return;
                                    const notes = prompt('Rejection reason:');
                                    try {
                                        await approveDoc({
                                            investigationId: id,
                                            documentId: doc.id,
                                            data: { status: 'rejected', ...(notes ? { notes } : {}) },
                                        }).unwrap();
                                    } catch (err: any) {
                                        alert(err?.data?.message || 'Failed to reject');
                                    }
                                }}
                                disabled={isApproving}
                                className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                            >
                                Reject
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ---------- Personal details sections content ----------
    const renderPersonalDetailsContent = () => {
        switch (personalDetailsSection) {
            case 'Personal Identification': {
                const addresses = parseJsonArray<{ date?: string; address?: string }>(investigation.addressHistory);
                const work = parseJsonArray<{
                    employer?: string;
                    address?: string;
                    contact?: string;
                    jobTitle?: string;
                    typeOfWork?: string;
                    startDate?: string;
                    endDate?: string;
                }>(investigation.workHistory);
                const education = parseJsonArray<{
                    institution?: string;
                    address?: string;
                    contact?: string;
                    educationLevel?: string;
                    qualification?: string;
                    startDate?: string;
                    endDate?: string;
                }>(investigation.educationHistory);

                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
                            Personal Identification &amp; Verification
                        </h3>
                        {(isSuperAdmin || isCustomerSuccess) && (
                            <PreviouslyReportedBanner
                                name={
                                    investigation.fullLegalName ||
                                    `${investigation.employeeFirstName ?? ''} ${investigation.employeeLastName ?? ''}`.trim()
                                }
                                email={investigation.employeeEmail}
                                phone={investigation.employeePhone}
                                nin={investigation.nationalIdentificationNumber ?? null}
                            />
                        )}
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Full Legal Name"
                                value={investigation.fullLegalName || employeeName}
                            />
                            <ReadOnlyField
                                label="Previous Names (including maiden/aliases):"
                                value={investigation.previousNames || investigation.employeeMiddleName || '---'}
                            />
                            <ReadOnlyField label="Alias / known-as:" value={investigation.alias || '---'} />
                            <ReadOnlyField label="Email:" value={investigation.employeeEmail || '---'} />
                            <ReadOnlyField label="Phone Number:" value={investigation.employeePhone || '---'} />
                            <ReadOnlyField
                                label="Gender:"
                                value={
                                    investigation.employeeGender ? (
                                        <span className="capitalize">{investigation.employeeGender}</span>
                                    ) : (
                                        '---'
                                    )
                                }
                            />
                            <ReadOnlyField
                                label="Date of Birth:"
                                value={
                                    investigation.dateOfBirth
                                        ? `${formatDate(investigation.dateOfBirth)}${age ? ` - ${age}yrs Old` : ''}`
                                        : '---'
                                }
                            />
                            <ReadOnlyField label="Place of Birth:" value={investigation.placeOfBirth || '---'} />
                            <ReadOnlyField label="Nationality:" value={investigation.nationality || '---'} />
                            <ReadOnlyField
                                label="Other Nationalities (if any):"
                                value={investigation.otherNationalities || '---'}
                            />
                            <ReadOnlyField label="State of Origin:" value={investigation.stateOfOrigin || '---'} />
                            <ReadOnlyField
                                label="Local Government Area:"
                                value={investigation.localGovernmentArea || '---'}
                            />
                            <ReadOnlyField
                                label="National Identification Number (NIN):"
                                value={investigation.nationalIdentificationNumber || '---'}
                            />

                            {/* Address History */}
                            <div>
                                <p className="text-sm font-semibold text-[#1b1b1b] mb-2">
                                    Address History (Past 10 Years):
                                </p>
                                <div className="border-l-[3px] border-[#d9d9d9] pl-4">
                                    {addresses.length > 0 ? (
                                        <div className="flex flex-col gap-6">
                                            {addresses.map((addr, i) => (
                                                <div key={i} className="flex flex-col gap-2">
                                                    <p className="text-sm">
                                                        <span className="font-normal">Date:</span>{' '}
                                                        <span className="font-bold text-[#1b1b1b]">
                                                            {addr.date || '---'}
                                                        </span>
                                                    </p>
                                                    <p className="text-sm">
                                                        <span className="font-normal">Address:</span>{' '}
                                                        <span className="font-bold text-[#1b1b1b]">
                                                            {addr.address || '---'}
                                                        </span>
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm font-bold text-[#1b1b1b]">---</p>
                                    )}
                                </div>
                            </div>

                            {/* Work History */}
                            <div>
                                <p className="text-sm font-semibold text-[#1b1b1b] mb-2">Work History:</p>
                                <div className="border-l-[3px] border-[#d9d9d9] pl-4">
                                    {work.length > 0 ? (
                                        <div className="flex flex-col gap-6">
                                            {work.map((w, i) => (
                                                <div key={i} className="flex flex-col gap-1.5">
                                                    <p className="text-sm font-bold text-[#1b1b1b]">
                                                        {w.jobTitle || '---'}
                                                        {w.employer ? ` at ${w.employer}` : ''}
                                                    </p>
                                                    {(w.startDate || w.endDate) && (
                                                        <p className="text-xs text-[#4e4d4d]">
                                                            {[w.startDate, w.endDate].filter(Boolean).join(' — ')}
                                                            {w.typeOfWork
                                                                ? ` · ${w.typeOfWork.replace(/_/g, ' ')}`
                                                                : ''}
                                                        </p>
                                                    )}
                                                    {w.address && <p className="text-xs text-[#4e4d4d]">{w.address}</p>}
                                                    {w.contact && (
                                                        <p className="text-xs text-[#4e4d4d]">Contact: {w.contact}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm font-bold text-[#1b1b1b]">---</p>
                                    )}
                                </div>
                            </div>

                            {/* Education History */}
                            <div>
                                <p className="text-sm font-semibold text-[#1b1b1b] mb-2">Education History:</p>
                                <div className="border-l-[3px] border-[#d9d9d9] pl-4">
                                    {education.length > 0 ? (
                                        <div className="flex flex-col gap-6">
                                            {education.map((e, i) => (
                                                <div key={i} className="flex flex-col gap-1.5">
                                                    <p className="text-sm font-bold text-[#1b1b1b]">
                                                        {e.qualification || '---'}
                                                        {e.institution ? ` · ${e.institution}` : ''}
                                                    </p>
                                                    {(e.startDate || e.endDate) && (
                                                        <p className="text-xs text-[#4e4d4d]">
                                                            {[e.startDate, e.endDate].filter(Boolean).join(' — ')}
                                                            {e.educationLevel
                                                                ? ` · ${e.educationLevel.replace(/_/g, ' ')}`
                                                                : ''}
                                                        </p>
                                                    )}
                                                    {e.address && <p className="text-xs text-[#4e4d4d]">{e.address}</p>}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm font-bold text-[#1b1b1b]">---</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            case 'Professional Discipline':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Professional Discipline</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Subject to disciplinary proceedings?"
                                value={investigation.hasDisciplinaryProceedings || '---'}
                            />
                            <ReadOnlyField
                                label="Disciplinary Proceedings Details"
                                value={investigation.disciplinaryProceedingsDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Professional licence revoked or suspended?"
                                value={investigation.hasLicenceRevoked || '---'}
                            />
                            <ReadOnlyField
                                label="Licence Revoked Details"
                                value={investigation.licenceRevokedDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Sanctioned by a regulatory body?"
                                value={investigation.hasRegulatoryBodySanction || '---'}
                            />
                            <ReadOnlyField
                                label="Regulatory Sanction Details"
                                value={investigation.regulatoryBodySanctionDetails || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Guarantors':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Guarantors</h3>
                        {id && (
                            <GuarantorsPanel
                                investigationId={id}
                                canManage={false}
                                guarantorTownWarning={investigation.guarantorTownWarning}
                                employmentTown={investigation.employmentTown}
                            />
                        )}
                    </div>
                );

            case 'Criminal & Law Enforcement Disclosure':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
                            Criminal &amp; Law Enforcement Disclosure
                        </h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Have you ever been convicted of a criminal offence?"
                                value={investigation.hasConviction || '---'}
                            />
                            <ReadOnlyField
                                label="Conviction Details"
                                value={investigation.convictionDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Currently under investigation?"
                                value={investigation.isUnderInvestigation || '---'}
                            />
                            <ReadOnlyField
                                label="Investigation Details"
                                value={investigation.investigationDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Charged with financial crimes?"
                                value={investigation.hasFinancialCrimeCharge || '---'}
                            />
                            <ReadOnlyField
                                label="Financial Crime Details"
                                value={investigation.financialCrimeDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Entered into a plea bargain?"
                                value={investigation.hasPleaBargain || '---'}
                            />
                            <ReadOnlyField
                                label="Plea Bargain Details"
                                value={investigation.pleaBargainDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Been disengaged or terminated?"
                                value={investigation.hasBeenDisengaged || '---'}
                            />
                            <ReadOnlyField
                                label="Disengagement Details"
                                value={investigation.disengagementDetails || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Financial & Directorship History':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Financial &amp; Directorship History</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Director of liquidated company?"
                                value={investigation.hasLiquidatedCompany || '---'}
                            />
                            <ReadOnlyField
                                label="Liquidated Company Details"
                                value={investigation.liquidatedCompanyDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Disqualified from acting as director?"
                                value={investigation.hasDirectorDisqualification || '---'}
                            />
                            <ReadOnlyField
                                label="Disqualification Details"
                                value={investigation.directorDisqualificationDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Currently a director/shareholder?"
                                value={investigation.isCurrentDirector || '---'}
                            />
                            <ReadOnlyField
                                label="Current Directorship Details"
                                value={investigation.currentDirectorshipDetails || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Political Exposure':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Political Exposure</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Politically Exposed Person (PEP)?"
                                value={investigation.isPep || '---'}
                            />
                            <ReadOnlyField
                                label="Held any public office?"
                                value={investigation.hasHeldPublicOffice || '---'}
                            />
                            <ReadOnlyField
                                label="Public Office Details"
                                value={investigation.publicOfficeDetails || '---'}
                            />
                            <ReadOnlyField label="Related to a PEP?" value={investigation.isRelatedToPep || '---'} />
                            <ReadOnlyField
                                label="Procurement-related investigation?"
                                value={investigation.hasProcurementInvestigation || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Workplace Conduct':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Workplace Conduct</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Dismissed for gross misconduct?"
                                value={investigation.hasMisconductDismissal || '---'}
                            />
                            <ReadOnlyField
                                label="Misconduct Details"
                                value={investigation.misconductDismissalDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Subject to workplace investigation?"
                                value={investigation.hasWorkplaceInvestigation || '---'}
                            />
                            <ReadOnlyField
                                label="Worked with vulnerable groups?"
                                value={investigation.hasWorkedWithVulnerableGroups || '---'}
                            />
                            <ReadOnlyField
                                label="Barred from leadership position?"
                                value={investigation.hasLeadershipBar || '---'}
                            />
                            <ReadOnlyField
                                label="Leadership Bar Details"
                                value={investigation.leadershipBarDetails || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Civil Claims':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Civil Claims</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Shareholder/fiduciary disputes?"
                                value={investigation.hasShareholderDisputes || '---'}
                            />
                            <ReadOnlyField
                                label="Judgments entered against you?"
                                value={investigation.hasJudgments || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Media Disclosure':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Media Disclosure</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Subject of adverse media coverage?"
                                value={investigation.hasAdverseMedia || '---'}
                            />
                            <ReadOnlyField
                                label="Adverse Media Details"
                                value={investigation.adverseMediaDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Subject to public accusation?"
                                value={investigation.hasPublicAccusation || '---'}
                            />
                            <ReadOnlyField
                                label="Undisclosed reputational matters?"
                                value={investigation.hasUndisclosedReputationalMatters || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Conflict Of Interest':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Conflict Of Interest</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Business conflict of interest?"
                                value={investigation.hasBusinessConflict || '---'}
                            />
                            <ReadOnlyField
                                label="Business Conflict Details"
                                value={investigation.businessConflictDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Procurement relationship conflict?"
                                value={investigation.hasProcurementRelationship || '---'}
                            />
                            <ReadOnlyField
                                label="Procurement Relationship Details"
                                value={investigation.procurementRelationshipDetails || '---'}
                            />
                            <ReadOnlyField
                                label="Sit on competing boards?"
                                value={investigation.hasCompetingBoard || '---'}
                            />
                            <ReadOnlyField
                                label="Competing Board Details"
                                value={investigation.competingBoardDetails || '---'}
                            />
                        </div>
                    </div>
                );

            case 'Data Protection & Consent':
                return (
                    <div>
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Data Protection &amp; Consent</h3>
                        <div className="flex flex-col gap-10">
                            <ReadOnlyField
                                label="Consent Given"
                                value={
                                    investigation.employeeConsentGiven
                                        ? `Yes - ${formatDate(investigation.dateConsentGiven)}`
                                        : 'Pending'
                                }
                            />
                            <ReadOnlyField
                                label="Criminal Background Check"
                                value={investigation.consentCriminalBackground ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Employment Reference Checks"
                                value={investigation.consentEmploymentReference ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Guarantor Verification"
                                value={investigation.consentGuarantorReference ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Address Verification"
                                value={investigation.consentAddressVerification ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Social Media Review"
                                value={investigation.consentSocialMediaReview ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Share Medical Report with Employer"
                                value={investigation.consentShareMedicalWithEmployer ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Court Record Searches"
                                value={investigation.consentCourtRecordSearches ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Lifestyle Audit"
                                value={investigation.consentLifestyleAudit ? 'Agreed' : 'Not agreed'}
                            />
                            <ReadOnlyField
                                label="Procurement Integrity Checks"
                                value={investigation.consentProcurementIntegrityChecks ? 'Agreed' : 'Not agreed'}
                            />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // ---------- Employer: Inline personal details ----------
    const renderEmployerPersonalDetails = () => {
        const fields = [
            { label: 'First Name', value: investigation.employeeFirstName },
            { label: 'Middle Name', value: investigation.employeeMiddleName || '---' },
            { label: 'Last Surname', value: investigation.employeeLastName },
            {
                label: 'Date Of Birth',
                value: investigation.dateOfBirth
                    ? `${formatDate(investigation.dateOfBirth)}${age ? ` - ${age}yrs Old` : ''}`
                    : '---',
            },
            { label: 'Nationality', value: investigation.nationality || '---' },
            { label: 'State of Origin', value: investigation.stateOfOrigin || '---' },
            { label: 'LGA', value: investigation.localGovernmentArea || '---' },
            { label: 'NIN', value: investigation.iin || '---' },
        ];

        return (
            <div className="space-y-6">
                {fields.map((field) => (
                    <div key={field.label} className="flex items-baseline gap-2">
                        <span className="text-sm text-gray-500">{field.label}:</span>
                        <span className="text-sm font-bold text-[#151515]">{field.value || '---'}</span>
                    </div>
                ))}
            </div>
        );
    };

    // ---------- Employer: Circular Risk Score ----------
    const RiskScoreCircle = ({ score }: { score: number }) => {
        const radius = 54;
        const circumference = 2 * Math.PI * radius;
        const strokeDashoffset = circumference - (score / 100) * circumference;
        const scoreColor = score <= 40 ? '#EF4444' : score <= 70 ? '#FF6B35' : '#22C55E';

        return (
            <div className="relative w-36 h-36 flex-shrink-0">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="10" />
                    <circle
                        cx="60"
                        cy="60"
                        r={radius}
                        fill="none"
                        stroke={scoreColor}
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        className="transition-all duration-700"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold" style={{ color: scoreColor }}>
                        {score}%
                    </span>
                </div>
            </div>
        );
    };

    // ---------- Employer: Risk Assessment Score view (read-only) ----------
    const renderEmployerRiskScore = () => {
        // Risk score is available as soon as the agent has scored and submitted a report.
        // Inline reports don't populate the legacy `reportUrl`, so fall back to the
        // status flag and the existence of inline title/details.
        const hasReport =
            !!investigation.reportUrl ||
            !!investigation.reportTitle ||
            !!investigation.reportDetails ||
            investigation.status === InvestigationStatus.UNDER_REVIEW ||
            investigation.status === InvestigationStatus.COMPLETED;

        if (!hasReport) {
            // Design 5 - Pending / Empty state
            return (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-16 h-16 rounded-full bg-[#FFF3ED] flex items-center justify-center mb-6">
                        <div className="w-6 h-6 rounded-full bg-[#FF6B35]" />
                    </div>
                    <p className="text-base font-semibold text-[#1b1b1b] mb-1">
                        Your Investigation Report is on its way.
                    </p>
                    <p className="text-sm text-[#4e4d4d]">Check back later for the completed report.</p>
                </div>
            );
        }

        // Design 3 - Risk Assessment Score with circular ring + read-only checkboxes
        const riskData = existingRiskAssessment;
        const riskPercent = riskData?.percentage ?? investigation.riskScorePercentage ?? 0;
        const riskLevelText = riskData?.riskLevel ?? investigation.riskLevel ?? '';

        // Build a set of selected keys from API data if available
        const apiSelectedKeys = new Set<string>();
        if (riskData?.categoryScores) {
            riskData.categoryScores.forEach((cs: any) => {
                apiSelectedKeys.add(`${cs.category}::${cs.selectedCriteria}`);
            });
        }

        return (
            <div>
                {/* Risk Score header with circle */}
                <div className="flex items-start gap-6 mb-10">
                    <RiskScoreCircle score={riskPercent} />
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-2">Risk Score</h3>
                        {riskLevelText && (
                            <p className="text-sm font-medium text-[#1b1b1b] mb-1">Level: {riskLevelText}</p>
                        )}
                        <p className="text-sm text-[#4e4d4d] leading-relaxed">
                            This risk score reflects the overall assessment of the background investigation. It
                            considers personal identification verification, criminal checks, financial history, and
                            other relevant factors to provide a comprehensive risk profile.
                        </p>
                        {riskData?.version && (
                            <p className="text-xs text-[#999] mt-2">
                                Assessment version {riskData.version}
                                {investigation.lastRiskAssessmentAt && (
                                    <>
                                        {' '}
                                        &middot; Last assessed{' '}
                                        {new Date(investigation.lastRiskAssessmentAt).toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                        })}
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                </div>

                {/* Read-only risk assessment categories */}
                <div className="space-y-8">
                    {RISK_ASSESSMENT_CATEGORIES.map((category) => (
                        <div key={category.title}>
                            <h4 className="text-sm font-bold text-[#1b1b1b] mb-3">{category.title}</h4>
                            <div className="space-y-2.5 pl-1">
                                {category.options.map((option) => {
                                    const key = `${category.category}::${option.label}`;
                                    const isChecked = riskData?.categoryScores
                                        ? apiSelectedKeys.has(key)
                                        : riskChecked[key] || false;
                                    const isPositive = option.score === 0;

                                    return (
                                        <div key={key} className="flex items-center gap-3">
                                            <div
                                                className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                                    isChecked
                                                        ? isPositive
                                                            ? 'bg-green-500 border-green-500'
                                                            : 'bg-[#1b1b1b] border-[#1b1b1b]'
                                                        : 'border-[#ccc]'
                                                }`}
                                            >
                                                {isChecked && (
                                                    <svg
                                                        className="w-2.5 h-2.5 text-white"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        strokeWidth={3}
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M5 13l4 4L19 7"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <span className="text-sm text-[#4e4d4d]">{option.label}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // ---------- Employer: Investigation Field Report view ----------
    const renderEmployerFieldReport = () => {
        // Use agentDocs filtered for investigation_report type
        const reportDocs = agentDocs?.filter((d: any) => d.documentType === 'investigation_report') || [];
        const hasInlineReport = !!(investigation.reportDetails || investigation.reportTitle);

        if (!hasInlineReport && !investigation.reportUrl && reportDocs.length === 0) {
            return (
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                        <div className="w-16 h-16 rounded-full bg-[#FFF3ED] flex items-center justify-center mb-6">
                            <div className="w-6 h-6 rounded-full bg-[#FF6B35]" />
                        </div>
                        <p className="text-base font-semibold text-[#1b1b1b] mb-1">
                            Your Investigation Report is on its way.
                        </p>
                        <p className="text-sm text-[#4e4d4d] max-w-md">
                            Findings appear here once the investigator submits the report and an admin approves it. Live
                            milestone progress is on the{' '}
                            <button
                                type="button"
                                onClick={() => setActiveTab('scope')}
                                className="text-[#FF6B35] font-medium hover:underline"
                            >
                                Scope of Work
                            </button>{' '}
                            tab.
                        </p>
                    </div>
                </div>
            );
        }

        return (
            <div>
                {hasInlineReport && (
                    <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                            <h3 className="text-sm font-bold text-[#1b1b1b] mb-1 truncate">
                                {investigation.reportTitle || 'Investigation Report'}
                            </h3>
                            <p className="text-xs text-[#4e4d4d]">
                                Open the full report formatted for review and print.
                            </p>
                        </div>
                        <Button
                            onClick={() => window.open(`/investigations/${id}/report`, '_blank', 'noopener,noreferrer')}
                            className="bg-[#151515] hover:bg-[#2a2a2a] text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
                        >
                            <Eye className="w-4 h-4" />
                            View Report
                        </Button>
                    </div>
                )}
                {/* Agent-uploaded investigation report documents */}
                {reportDocs.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {reportDocs.map((doc: any) => (
                            <DocumentCard key={doc.id} doc={doc} showStatusBadge />
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                    <button
                        type="button"
                        onClick={() => window.open(`/investigations/${id}/report`, '_blank', 'noopener,noreferrer')}
                        className="text-left border border-[#e0e0e0] rounded-xl overflow-hidden hover:border-[#FF6B35] transition-colors cursor-pointer"
                        title="Open the full report in a new tab"
                    >
                        {/* File preview placeholder */}
                        <div className="w-full aspect-[4/3] bg-[#f5f5f5] flex items-center justify-center relative group">
                            <div className="space-y-1.5 px-6 w-full">
                                {[...Array(5)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="h-1.5 bg-[#ddd] rounded-full"
                                        style={{ width: `${70 + Math.random() * 30}%` }}
                                    />
                                ))}
                            </div>
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <Eye className="w-6 h-6 text-white" />
                            </div>
                        </div>

                        {/* Title + actions */}
                        <div className="px-3 py-2.5">
                            <p className="text-xs font-medium text-[#1b1b1b] truncate mb-2">Investigation Report</p>
                            <div className="flex items-center gap-1">
                                <span
                                    className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] transition-colors inline-flex"
                                    title="View"
                                >
                                    <Eye className="w-3.5 h-3.5" />
                                </span>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            {/* Always-mounted hidden input for per-document replace/overwrite (SA / CS). */}
            {canManageDocs && (
                <input
                    ref={replaceInputRef}
                    type="file"
                    accept="application/pdf,.pdf,.doc,.docx,image/jpeg,image/png"
                    onChange={handleReplaceFileSelected}
                    className="hidden"
                />
            )}
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col">
                {/* Header/Navbar */}
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={3}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'U'}
                    only={true}
                    className="border-b border-gray-100"
                />

                {/* Content */}
                <div className="flex-1 p-4 md:p-8 lg:p-10 flex flex-col">
                    {/* Back Button + Employee Name + Action Buttons */}
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center w-9 h-9 rounded-full bg-[#f0f0f0] hover:bg-[#e0e0e0] transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-[#1b1b1b]" />
                        </button>
                        <h1 className="text-xl font-bold text-[#1b1b1b]">{employeeName}</h1>

                        {canRequestInfo && (
                            <div className="flex flex-wrap items-center gap-2 md:gap-3 ml-auto">
                                {/* Preview Report: available to Super Admin whenever a
                                    report exists (submitted for review or completed). */}
                                {isSuperAdmin &&
                                    (investigation.status === InvestigationStatus.UNDER_REVIEW ||
                                        investigation.status === InvestigationStatus.COMPLETED) && (
                                        <Button
                                            onClick={() =>
                                                window.open(
                                                    `/investigations/${id}/report`,
                                                    '_blank',
                                                    'noopener,noreferrer',
                                                )
                                            }
                                            variant="outline"
                                            className="border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                                        >
                                            <Eye className="w-4 h-4" />
                                            Preview Report
                                        </Button>
                                    )}
                                {/* Review action only applies while the report awaits a decision. */}
                                {isSuperAdmin && investigation.status === InvestigationStatus.UNDER_REVIEW && (
                                    <Button
                                        onClick={() => setShowReviewModal(true)}
                                        className="bg-amber-500 hover:bg-amber-600 text-white px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                                    >
                                        <CheckCircle2 className="w-4 h-4" />
                                        Review
                                    </Button>
                                )}
                                <Button
                                    onClick={() => setShowRequestInfoModal(true)}
                                    variant="outline"
                                    className="border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                                >
                                    <ClipboardList className="w-4 h-4" />
                                    Request Info
                                </Button>
                                <Button
                                    onClick={() => setShowNudgeModal(true)}
                                    variant="outline"
                                    className="border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                                >
                                    <BellRing className="w-4 h-4" />
                                    Nudge
                                </Button>
                                {/* Retrigger consent — Super Admin, only before the form is submitted.
                                    Submitted employees get "Request Info" instead. */}
                                {isSuperAdmin && !investigation.isFormSubmitted && (
                                    <Button
                                        onClick={() => {
                                            setRetriggerError('');
                                            setShowRetriggerModal(true);
                                        }}
                                        variant="outline"
                                        className="border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Retrigger Consent
                                    </Button>
                                )}
                                {isSuperAdmin && investigation.employer && (
                                    <Button
                                        onClick={() => setShowEmployerModal(true)}
                                        variant="outline"
                                        className="border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium"
                                    >
                                        Employer Details
                                    </Button>
                                )}
                                {isSuperAdmin && (
                                    <Button
                                        onClick={() => setShowDeleteModal(true)}
                                        variant="outline"
                                        className="border-red-200 text-red-600 hover:bg-red-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* SLA Countdown — renders only when this investigation has an active SLA stage */}
                    {investigation.id && <SlaCountdown investigationId={investigation.id} className="mb-4" />}

                    {/* Alert Banner */}
                    <div className="flex items-start gap-2.5 mb-6 py-2">
                        <div className="w-5 h-5 rounded-full bg-[#FF6600] mt-0.5 flex-shrink-0 flex items-center justify-center">
                            <span className="text-white text-[10px] font-bold">!</span>
                        </div>
                        <p className="text-sm text-[#747474] leading-relaxed">
                            This page displays comprehensive details about the background check investigation, including
                            employee information, investigation status, assigned agent, uploaded documents, and
                            investigation reports. Track the progress and review all submitted materials in one place.
                        </p>
                    </div>

                    {/* Two-column layout: left sidebar + content */}
                    <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                        {/* Left Sidebar - Request Details */}
                        <div className="lg:w-80 flex-shrink-0 overflow-y-auto">
                            <div className="bg-[#f5f5f5] rounded-lg p-4 md:p-6">
                                <h2 className="text-xl font-bold text-[#181d27] mb-6">Request Details</h2>

                                <div className="space-y-6">
                                    <p className="text-sm text-[#717680]">
                                        Employee Name: <span className="font-bold text-[#252b37]">{employeeName}</span>
                                    </p>

                                    <div className="flex items-center gap-2.5">
                                        <span className="text-sm text-[#717680]">Check Type:</span>
                                        <Badge
                                            variant="outline"
                                            className={`text-[13px] px-2.5 py-1 rounded font-medium capitalize border-0 ${
                                                investigation.checkType === CheckType.STANDARD
                                                    ? 'bg-[rgba(52,72,240,0.12)] text-[#3448f0]'
                                                    : 'bg-orange-50 text-[#FF6B35]'
                                            }`}
                                        >
                                            {investigation.checkType}
                                        </Badge>
                                    </div>

                                    {/* Show Check By in report tab */}
                                    {activeTab === 'report' && investigation.agent && (
                                        <p className="text-sm text-[#717680]">
                                            Checked By:{' '}
                                            <span className="font-bold text-[#252b37]">
                                                {investigation.agent.firstName} {investigation.agent.lastName}
                                            </span>
                                        </p>
                                    )}

                                    <p className="text-sm text-[#717680]">
                                        Date Requested:{' '}
                                        <span className="font-bold text-[#252b37]">
                                            {formatDate(investigation.dateRequested || investigation.createdAt)}
                                        </span>
                                    </p>

                                    <p className="text-sm text-[#717680]">
                                        Report Submitted:{' '}
                                        <span className="font-bold text-[#252b37]">
                                            {formatDate(investigation.reportSubmittedDate)}
                                        </span>
                                    </p>

                                    <div className="flex items-center gap-2.5">
                                        <span className="text-sm text-[#717680]">Status:</span>
                                        <Badge
                                            variant="outline"
                                            className={`text-[13px] px-2.5 py-1 rounded font-medium border-0 ${getStatusColor(investigation.status)}`}
                                        >
                                            {getStatusLabel(investigation.status)}
                                        </Badge>
                                    </div>

                                    {/* Amount paid is the employer's billing figure \u2014 hidden from
                                        field agents, who shouldn't see what the employer was charged. */}
                                    {!isAgent && (
                                        <p className="text-sm text-[#717680]">
                                            Amount Paid:{' '}
                                            <span className="font-bold text-[#252b37]">
                                                {investigation.isPaid && investigation.amountPaid
                                                    ? new Intl.NumberFormat('en-NG', {
                                                          style: 'currency',
                                                          currency: 'NGN',
                                                          minimumFractionDigits: 0,
                                                      }).format(investigation.amountPaid)
                                                    : investigation.checkType === CheckType.ENHANCED
                                                      ? '\u20A678,900'
                                                      : '\u20A656,900'}
                                            </span>
                                        </p>
                                    )}

                                    {/* Support code + Message employee — employer only. The
                      support code is how the employee identifies themselves
                      when they email or open support; the message button is
                      a shortcut into the Conversations tab. */}
                                    {isEmployer && investigation.supportCode && (
                                        <div className="pt-1 flex flex-col gap-2">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[11px] uppercase tracking-wider text-[#717680] font-semibold">
                                                    Support code
                                                </span>
                                                <div className="flex items-center gap-2 bg-[#fafafa] border border-[#e0e0e0] rounded-lg px-3 py-2">
                                                    <span className="flex-1 font-mono text-sm font-semibold text-[#1b1b1b] tracking-[0.04em] truncate">
                                                        {investigation.supportCode}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(
                                                                    investigation.supportCode!,
                                                                );
                                                                setSupportCodeCopied(true);
                                                                window.setTimeout(
                                                                    () => setSupportCodeCopied(false),
                                                                    1500,
                                                                );
                                                            } catch {
                                                                /* clipboard rejected */
                                                            }
                                                        }}
                                                        className="text-xs font-medium text-[#FF6B35] hover:underline"
                                                    >
                                                        {supportCodeCopied ? 'Copied' : 'Copy'}
                                                    </button>
                                                </div>
                                            </div>
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    setConversationsStartCompose(true);
                                                    setActiveTab('conversations');
                                                }}
                                                className="w-full bg-[#151515] hover:bg-[#2a2a2a] text-white px-3 py-2 rounded-xl text-xs font-medium"
                                            >
                                                Message employee
                                            </Button>
                                        </div>
                                    )}

                                    {/* Validation Button for Super Admin */}
                                    {isSuperAdmin && investigation.status === InvestigationStatus.UNDER_REVIEW && (
                                        <div className="pt-1">
                                            <Button
                                                onClick={() => setShowValidationModal(true)}
                                                disabled={isValidating}
                                                className="w-full bg-[#1b1b1b] hover:bg-[#333] text-white px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                Validate Investigation
                                            </Button>
                                        </div>
                                    )}

                                    {/* Assignment Buttons for Super Admin */}
                                    {isSuperAdmin && (
                                        <div className="pt-1 flex flex-col gap-2">
                                            <Button
                                                onClick={() => setShowAssignModal(true)}
                                                variant="outline"
                                                className="w-full border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35]/10 px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                                            >
                                                <UserPlus className="w-3.5 h-3.5" />
                                                {investigation.agent ? 'Reassign Agent' : 'Assign Agent'}
                                            </Button>
                                            <Button
                                                onClick={() => setShowAssignCsModal(true)}
                                                variant="outline"
                                                className="w-full border-[#e0e0e0] text-[#1b1b1b] hover:bg-[#fafafa] px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                                            >
                                                <UserPlus className="w-3.5 h-3.5" />
                                                {investigation.customerSuccess
                                                    ? 'Reassign Customer Success'
                                                    : 'Assign Customer Success'}
                                            </Button>
                                            <Button
                                                onClick={() => setShowAssignMedicalModal(true)}
                                                variant="outline"
                                                className="w-full border-[#e0e0e0] text-[#1b1b1b] hover:bg-[#fafafa] px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                                            >
                                                <UserPlus className="w-3.5 h-3.5" />
                                                {investigation.medicalUser
                                                    ? 'Reassign Medical User'
                                                    : 'Assign Medical User'}
                                            </Button>
                                            <Button
                                                onClick={() => setShowAssignNotaryModal(true)}
                                                variant="outline"
                                                className="w-full border-[#e0e0e0] text-[#1b1b1b] hover:bg-[#fafafa] px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                                            >
                                                <UserPlus className="w-3.5 h-3.5" />
                                                {investigation.notaryUser ? 'Reassign Notary' : 'Assign Notary'}
                                            </Button>
                                            <Button
                                                onClick={() => setShowAssignPoliceModal(true)}
                                                variant="outline"
                                                className="w-full border-[#e0e0e0] text-[#1b1b1b] hover:bg-[#fafafa] px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                                            >
                                                <UserPlus className="w-3.5 h-3.5" />
                                                {investigation.policeUser ? 'Reassign Police' : 'Assign Police'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Right Content Area */}
                        <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
                            {/* Main Toggle Buttons */}
                            <div className="flex flex-wrap gap-2 md:gap-4 mb-4 md:mb-6">
                                {/* Scope of work — orientation tab. Available to everyone with
                    access (the brief endpoint is auth'd for agent/employer/admin)
                    but agents land on it by default. */}
                                <button
                                    onClick={() => setActiveTab('scope')}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                        activeTab === 'scope'
                                            ? 'bg-[#151515] text-white'
                                            : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                                    }`}
                                >
                                    Scope of Work
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab('details');
                                        setActiveSubTab('personal');
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                        activeTab === 'details'
                                            ? 'bg-[#151515] text-white'
                                            : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                                    }`}
                                >
                                    Details Submitted
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveTab('report');
                                        setActiveSubTab('personal');
                                        if (isEmployer) setEmployerReportSubTab('risk-score');
                                    }}
                                    className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                        activeTab === 'report'
                                            ? 'bg-[#151515] text-white'
                                            : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                                    }`}
                                >
                                    {isAgent ? 'Submit Investigation Report' : 'Investigation Report'}
                                </button>

                                {isEmployer && (
                                    <button
                                        onClick={() => {
                                            setActiveTab('conversations');
                                            setConversationsStartCompose(false);
                                        }}
                                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                            activeTab === 'conversations'
                                                ? 'bg-[#151515] text-white'
                                                : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                                        }`}
                                    >
                                        Conversations
                                    </button>
                                )}
                            </div>

                            {/* ============ SCOPE OF WORK TAB ============ */}
                            {activeTab === 'scope' && investigation.id && (
                                <div className="flex-1 overflow-y-auto min-h-0">
                                    {investigation.isPaid && investigation.expectedReportReadyFrom && (
                                        <div className="mb-6 flex items-start gap-3 bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-xl px-4 py-3">
                                            <Clock className="w-5 h-5 text-[#FF6B35] shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-semibold text-[#1b1b1b]">
                                                    Report expected within 21 working days
                                                </p>
                                                <p className="text-xs text-[#4e4d4d] mt-0.5">
                                                    {isEmployer
                                                        ? 'Your completed background report is expected within this window.'
                                                        : 'Expected turnaround window shared with the employer.'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    <AgentBriefCard investigationId={investigation.id} />

                                    {/* Category, medical add-on, assigned CS/medical user */}
                                    <InvestigationAddonsCard investigation={investigation} />

                                    {/* CS progress checklist — read-only for Super Admin oversight. */}
                                    {isSuperAdmin && investigation.id && (
                                        <div className="mt-6">
                                            <CsChecklistPanel investigationId={investigation.id} />
                                        </div>
                                    )}

                                    {/* Social handles + review consent — always shown (every role that
                                        works the case needs these, agents especially). */}
                                    <SocialMediaPanel
                                        handles={investigation.socialMediaHandles}
                                        consentGiven={investigation.consentSocialMediaReview}
                                        className="mt-6"
                                    />

                                    {/* Employers see live milestone progress next to the scope so they
                                        can track what the investigator is delivering against. */}
                                    {isEmployer && (
                                        <div className="mt-6">
                                            <MilestoneTimeline investigationId={investigation.id} />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============ DETAILS SUBMITTED TAB ============ */}
                            {activeTab === 'details' && (
                                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                                    {/* Sub-tabs: Personal Details | Documents */}
                                    <div className="relative mb-6">
                                        <div className="flex gap-10">
                                            <button
                                                onClick={() => {
                                                    setActiveSubTab('personal');
                                                    setShowUploadArea(false);
                                                }}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    activeSubTab === 'personal'
                                                        ? 'text-[#1b1b1b] font-bold'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Personal Details
                                                {activeSubTab === 'personal' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#1b1b1b]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setActiveSubTab('documents');
                                                    setShowUploadArea(false);
                                                }}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    activeSubTab === 'documents'
                                                        ? 'text-[#1b1b1b] font-bold'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Documents
                                                {activeSubTab === 'documents' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#1b1b1b]" />
                                                )}
                                            </button>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#e0e0e0]" />
                                    </div>

                                    {/* Personal Details Content */}
                                    {activeSubTab === 'personal' && (
                                        <div className="flex flex-col flex-1 min-h-0">
                                            {isEmployer ? (
                                                /* Employer: Simple inline label-value pairs */
                                                <div className="py-2 flex-1 overflow-y-auto">
                                                    {renderEmployerPersonalDetails()}
                                                </div>
                                            ) : (
                                                /* Agent / Super Admin: Bordered sections with tab pills */
                                                <div className="flex flex-col flex-1 min-h-0">
                                                    {/* Left vertical progress bar + content */}
                                                    <div className="flex gap-4 flex-1 min-h-0 overflow-y-auto pr-2">
                                                        {/* Progress track */}
                                                        <div className="w-1 bg-[#efefef] rounded-[56px] flex-shrink-0 relative">
                                                            <div
                                                                className="absolute top-0 left-0 w-full bg-[#474747] rounded-[56px] transition-all duration-300"
                                                                style={{
                                                                    height: `${((DETAILS_SECTION_TABS.indexOf(personalDetailsSection) + 1) / DETAILS_SECTION_TABS.length) * 100}%`,
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Section content */}
                                                        <div className="flex-1 min-w-0">
                                                            {renderPersonalDetailsContent()}
                                                        </div>
                                                    </div>

                                                    {/* Bottom tab pills */}
                                                    <div className="mt-4 pt-4 flex-shrink-0">
                                                        <div className="flex flex-wrap gap-2 md:gap-4">
                                                            {DETAILS_SECTION_TABS.map((tab) => (
                                                                <button
                                                                    key={tab}
                                                                    onClick={() => setPersonalDetailsSection(tab)}
                                                                    className={`px-4 py-3 rounded-full text-xs font-normal transition-colors whitespace-nowrap ${
                                                                        personalDetailsSection === tab
                                                                            ? 'bg-[#1b1b1b] text-white'
                                                                            : 'bg-[#dcdcdc] text-[#1b1b1b] hover:bg-[#ccc]'
                                                                    }`}
                                                                >
                                                                    {tab}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Documents Content */}
                                    {activeSubTab === 'documents' && (
                                        <div className="flex flex-col gap-4">
                                            {/* Agent / Super Admin document upload — any document type. */}
                                            {(isAgent || isSuperAdmin) && (
                                                <div className="rounded-xl border border-[#e0e0e0] p-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-medium text-[#4e4d4d] mb-1">
                                                            Document type
                                                        </label>
                                                        <select
                                                            value={uploadDocType}
                                                            onChange={(e) => setUploadDocType(e.target.value)}
                                                            className="w-full px-3 h-11 border border-[#e0e0e0] rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
                                                        >
                                                            {AGENT_DOCUMENT_TYPES.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="application/pdf,.pdf,.doc,.docx,image/jpeg,image/png"
                                                        onChange={handleFileUpload}
                                                        className="hidden"
                                                    />
                                                    <Button
                                                        onClick={() => fileInputRef.current?.click()}
                                                        disabled={isUploading || isUploadingStaff}
                                                        className="shrink-0 bg-[#FF6B35] hover:bg-[#FF5722] text-white px-5 h-11 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                                                    >
                                                        {isUploading || isUploadingStaff ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <CloudUpload className="w-4 h-4" />
                                                        )}
                                                        Upload document
                                                    </Button>
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                                {employeeDocs && employeeDocs.length > 0 ? (
                                                    employeeDocs.map((doc: any) => (
                                                        <DocumentCard key={doc.id} doc={doc} />
                                                    ))
                                                ) : investigation.documents && investigation.documents.length > 0 ? (
                                                    investigation.documents.map((doc) => (
                                                        <DocumentCard key={doc.id} doc={doc} />
                                                    ))
                                                ) : (
                                                    <div className="col-span-full text-center py-12">
                                                        <p className="text-[#4e4d4d]">No documents uploaded yet</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============ INVESTIGATION REPORT TAB ============ */}
                            {activeTab === 'report' && (
                                <div className="flex flex-col flex-1">
                                    {isEmployer ? (
                                        /* Employer: "Risk Assessment Score" | "Investigation Field Report" sub-tabs */
                                        <div className="flex gap-6 mb-6 border-b border-[#e0e0e0] overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                            <button
                                                onClick={() => setEmployerReportSubTab('risk-score')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    employerReportSubTab === 'risk-score'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Risk Assessment Score
                                                {employerReportSubTab === 'risk-score' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setEmployerReportSubTab('field-report')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    employerReportSubTab === 'field-report'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Investigation Field Report
                                                {employerReportSubTab === 'field-report' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setEmployerReportSubTab('guarantors')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    employerReportSubTab === 'guarantors'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Guarantors
                                                {employerReportSubTab === 'guarantors' && (
                                                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                                                )}
                                            </button>
                                        </div>
                                    ) : (
                                        /* Agent / Super Admin: "Risk Assessment" | "Field Report" sub-tabs */
                                        <div className="flex gap-5 md:gap-8 mb-6 border-b border-[#e0e0e0] overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                            <button
                                                onClick={() => setReportSubTab('risk-assessment')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    reportSubTab === 'risk-assessment'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Risk Assessment
                                                {reportSubTab === 'risk-assessment' && (
                                                    <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#FF6B35]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setReportSubTab('field-report')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    reportSubTab === 'field-report'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Field Report
                                                {reportSubTab === 'field-report' && (
                                                    <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#FF6B35]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setReportSubTab('visits')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    reportSubTab === 'visits'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Visits
                                                {reportSubTab === 'visits' && (
                                                    <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#FF6B35]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setReportSubTab('milestones')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    reportSubTab === 'milestones'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Milestones
                                                {missingMilestones.length > 0 && (
                                                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                                                        {missingMilestones.length}
                                                    </span>
                                                )}
                                                {milestonesData &&
                                                    milestonesData.totalMilestones > 0 &&
                                                    missingMilestones.length === 0 && (
                                                        <span className="ml-2 text-[10px] text-[#717680] font-medium">
                                                            {milestonesData.submittedMilestones}/
                                                            {milestonesData.totalMilestones}
                                                        </span>
                                                    )}
                                                {reportSubTab === 'milestones' && (
                                                    <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#FF6B35]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setReportSubTab('checklist')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    reportSubTab === 'checklist'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Checklist
                                                {missingChecklistItems.length > 0 && (
                                                    <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                                                        {missingChecklistItems.length}
                                                    </span>
                                                )}
                                                {reportSubTab === 'checklist' && (
                                                    <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#FF6B35]" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setReportSubTab('guarantors')}
                                                className={`pb-3 text-sm font-medium transition-colors relative shrink-0 whitespace-nowrap ${
                                                    reportSubTab === 'guarantors'
                                                        ? 'text-[#1b1b1b]'
                                                        : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                                                }`}
                                            >
                                                Guarantors
                                                {investigation.guarantorTownWarning && (
                                                    <span className="ml-2 inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-[#FF6B35] text-white text-[10px] font-bold">
                                                        !
                                                    </span>
                                                )}
                                                {reportSubTab === 'guarantors' && (
                                                    <div className="absolute -bottom-px left-0 right-0 h-0.5 bg-[#FF6B35]" />
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {/* Employer Report Content */}
                                    {isEmployer && employerReportSubTab === 'risk-score' && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            {renderEmployerRiskScore()}
                                        </div>
                                    )}
                                    {isEmployer && employerReportSubTab === 'field-report' && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            {renderEmployerFieldReport()}
                                        </div>
                                    )}
                                    {isEmployer && employerReportSubTab === 'guarantors' && id && investigation && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            <GuarantorsPanel
                                                investigationId={id}
                                                canManage={false}
                                                guarantorTownWarning={investigation.guarantorTownWarning}
                                                employmentTown={investigation.employmentTown}
                                            />
                                        </div>
                                    )}

                                    {/* Risk Assessment Content (Agent / Super Admin) */}
                                    {!isEmployer && reportSubTab === 'risk-assessment' && (
                                        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                                            <p className="text-sm text-[#4e4d4d] leading-relaxed mb-4">
                                                Review the risk assessment findings below. Each category represents a
                                                distinct area of the background investigation. Select the applicable
                                                findings for each category based on your investigation results.
                                            </p>

                                            {/* Version info & history selector */}
                                            {(existingRiskAssessment?.version ||
                                                (riskHistory && riskHistory.length > 0)) && (
                                                <div className="flex items-center gap-4 mb-6 flex-shrink-0">
                                                    {existingRiskAssessment?.version && (
                                                        <div className="flex items-center gap-2 text-xs text-[#4e4d4d] bg-[#f5f5f5] rounded-lg px-3 py-2">
                                                            <span>
                                                                Current:{' '}
                                                                <span className="font-semibold text-[#1b1b1b]">
                                                                    v{existingRiskAssessment.version}
                                                                </span>
                                                            </span>
                                                            <span className="text-[#ccc]">|</span>
                                                            <span
                                                                className={`font-medium capitalize ${
                                                                    existingRiskAssessment.riskLevel === 'low'
                                                                        ? 'text-green-600'
                                                                        : existingRiskAssessment.riskLevel ===
                                                                            'moderate'
                                                                          ? 'text-yellow-600'
                                                                          : existingRiskAssessment.riskLevel === 'high'
                                                                            ? 'text-orange-600'
                                                                            : 'text-red-600'
                                                                }`}
                                                            >
                                                                {existingRiskAssessment.riskLevel} risk
                                                            </span>
                                                            <span className="text-[#ccc]">|</span>
                                                            <span>{existingRiskAssessment.percentage}%</span>
                                                        </div>
                                                    )}
                                                    {riskHistory && riskHistory.length > 1 && (
                                                        <select
                                                            value={selectedHistoryVersion ?? ''}
                                                            onChange={(e) => {
                                                                const ver = e.target.value
                                                                    ? Number(e.target.value)
                                                                    : null;
                                                                setSelectedHistoryVersion(ver);
                                                                if (ver !== null) {
                                                                    const historyEntry = riskHistory.find(
                                                                        (h) => h.version === ver,
                                                                    );
                                                                    if (historyEntry) {
                                                                        const checked: Record<string, boolean> = {};
                                                                        const rationale: Record<string, string> = {};
                                                                        historyEntry.categoryScores.forEach((cs) => {
                                                                            checked[
                                                                                `${cs.category}::${cs.selectedCriteria}`
                                                                            ] = true;
                                                                            if (cs.rationale)
                                                                                rationale[cs.category] = cs.rationale;
                                                                        });
                                                                        setRiskChecked(checked);
                                                                        setRiskRationale(rationale);
                                                                    }
                                                                } else if (existingRiskAssessment?.categoryScores) {
                                                                    const checked: Record<string, boolean> = {};
                                                                    const rationale: Record<string, string> = {};
                                                                    existingRiskAssessment.categoryScores.forEach(
                                                                        (cs) => {
                                                                            checked[
                                                                                `${cs.category}::${cs.selectedCriteria}`
                                                                            ] = true;
                                                                            if (cs.rationale)
                                                                                rationale[cs.category] = cs.rationale;
                                                                        },
                                                                    );
                                                                    setRiskChecked(checked);
                                                                    setRiskRationale(rationale);
                                                                }
                                                            }}
                                                            className="text-xs border border-[#e0e0e0] rounded-lg px-2 py-1.5 text-[#4e4d4d]"
                                                        >
                                                            <option value="">
                                                                Current (v{existingRiskAssessment?.version})
                                                            </option>
                                                            {riskHistory.map((h) => (
                                                                <option key={h.version} value={h.version}>
                                                                    Version {h.version} — {h.riskLevel} ({h.percentage}
                                                                    %)
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>
                                            )}

                                            <div className="space-y-8 flex-1 overflow-y-auto pr-2 min-h-0">
                                                {RISK_ASSESSMENT_CATEGORIES.map((category) => (
                                                    <div key={category.title}>
                                                        <h4 className="text-sm font-bold text-[#1b1b1b] mb-3 sticky top-0 bg-white py-1 z-10">
                                                            {category.title}
                                                        </h4>
                                                        <div className="space-y-2.5 pl-1">
                                                            {category.options.map((option) => {
                                                                const key = `${category.category}::${option.label}`;
                                                                const isViewingHistory =
                                                                    selectedHistoryVersion !== null;
                                                                return (
                                                                    <label
                                                                        key={key}
                                                                        className={`flex items-center gap-3 group ${isViewingHistory ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                                                                    >
                                                                        <div
                                                                            className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                                                                riskChecked[key]
                                                                                    ? 'bg-[#1b1b1b] border-[#1b1b1b]'
                                                                                    : 'border-[#ccc] group-hover:border-[#999]'
                                                                            }`}
                                                                            onClick={() => {
                                                                                if (isViewingHistory) return;
                                                                                setRiskChecked((prev) => ({
                                                                                    ...prev,
                                                                                    [key]: !prev[key],
                                                                                }));
                                                                            }}
                                                                        >
                                                                            {riskChecked[key] && (
                                                                                <svg
                                                                                    className="w-2.5 h-2.5 text-white"
                                                                                    fill="none"
                                                                                    viewBox="0 0 24 24"
                                                                                    stroke="currentColor"
                                                                                    strokeWidth={3}
                                                                                >
                                                                                    <path
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        d="M5 13l4 4L19 7"
                                                                                    />
                                                                                </svg>
                                                                            )}
                                                                        </div>
                                                                        <span className="text-sm text-[#4e4d4d] group-hover:text-[#1b1b1b] transition-colors">
                                                                            {option.label}
                                                                        </span>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {isAgent &&
                                                selectedHistoryVersion === null &&
                                                // Visible during the normal IN_PROGRESS edit window AND
                                                // also after the report has been submitted (UNDER_REVIEW)
                                                // *if* the risk assessment was somehow skipped — agents
                                                // shouldn't get locked out of submitting a missing
                                                // assessment just because the report went to review.
                                                (investigation.status === InvestigationStatus.IN_PROGRESS ||
                                                    (investigation.status === InvestigationStatus.UNDER_REVIEW &&
                                                        !hasRiskAssessment)) && (
                                                    <div className="mt-6 flex-shrink-0">
                                                        <button
                                                            onClick={handleSubmitRiskAssessment}
                                                            disabled={isSubmittingRisk}
                                                            className="bg-[#FF6B35] hover:bg-[#e05a2a] text-white px-6 py-3 rounded-lg text-sm font-medium disabled:opacity-50"
                                                        >
                                                            {isSubmittingRisk
                                                                ? 'Submitting...'
                                                                : existingRiskAssessment?.version
                                                                  ? 'Resubmit Risk Assessment'
                                                                  : 'Submit Risk Assessment'}
                                                        </button>
                                                        {investigation.status === InvestigationStatus.UNDER_REVIEW &&
                                                            !hasRiskAssessment && (
                                                                <p className="text-xs text-[#FF6B35] mt-2">
                                                                    The report was submitted without a risk assessment —
                                                                    please complete this so reviewers have the full
                                                                    picture.
                                                                </p>
                                                            )}
                                                    </div>
                                                )}
                                            {selectedHistoryVersion !== null && (
                                                <div className="mt-4 flex-shrink-0">
                                                    <p className="text-xs text-[#999] italic">
                                                        Viewing version {selectedHistoryVersion} (read-only). Select
                                                        "Current" to edit.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Field Report Content (Agent / Super Admin) */}
                                    {!isEmployer && reportSubTab === 'field-report' && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            {(() => {
                                                const isUnderReview =
                                                    investigation.status === InvestigationStatus.UNDER_REVIEW;
                                                const isCompleted =
                                                    investigation.status === InvestigationStatus.COMPLETED;
                                                // Reviewers (Super Admin / Customer Success) can edit a
                                                // report once it's been submitted (UNDER_REVIEW) so they
                                                // can correct or clean up findings before approving.
                                                // Editing a completed report stays intentionally
                                                // available — the validation flow handles approve/reject;
                                                // this is just for content tweaks.
                                                const canAdminEdit =
                                                    isReportEditor && (isUnderReview || isCompleted) && adminEditMode;
                                                const canEdit =
                                                    (isAgent &&
                                                        investigation.status === InvestigationStatus.IN_PROGRESS) ||
                                                    canAdminEdit;

                                                return (
                                                    <div className="flex flex-col gap-6 pb-6">
                                                        {/* Status banners */}
                                                        {isAgent && isUnderReview && (
                                                            <div className="bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 text-sm text-[#4e4d4d]">
                                                                Your report has been submitted and is currently
                                                                <span className="font-semibold text-[#1b1b1b]">
                                                                    {' '}
                                                                    under review
                                                                </span>
                                                                . You'll be able to edit again only if a reviewer
                                                                rejects it.
                                                            </div>
                                                        )}
                                                        {isAgent && isCompleted && (
                                                            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700">
                                                                This report has been approved and the investigation is
                                                                complete.
                                                            </div>
                                                        )}
                                                        {investigation.isReportValidated === false &&
                                                            investigation.validationNotes && (
                                                                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                                                                    <p className="font-semibold mb-1">
                                                                        Reviewer feedback
                                                                    </p>
                                                                    <p className="whitespace-pre-wrap">
                                                                        {investigation.validationNotes}
                                                                    </p>
                                                                </div>
                                                            )}

                                                        {/* Reviewer edit toggle — shows once an agent has submitted the
                                report. Lets Super Admin / Customer Success tidy up wording before sharing. */}
                                                        {isReportEditor &&
                                                            !adminEditMode &&
                                                            (isUnderReview || isCompleted) &&
                                                            (investigation.reportTitle ||
                                                                investigation.reportDetails) && (
                                                                <div className="flex items-center justify-between gap-3 bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 flex-wrap">
                                                                    <p className="text-sm text-[#4e4d4d]">
                                                                        <span className="font-semibold text-[#1b1b1b]">
                                                                            Need to revise this report?
                                                                        </span>{' '}
                                                                        You can edit the title, summary, and detailed
                                                                        findings before sharing it with the employer.
                                                                    </p>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setAdminEditMode(true);
                                                                            setReportSaveError('');
                                                                            setReportSaveSuccess('');
                                                                        }}
                                                                        className="bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-2 rounded-lg text-sm font-medium"
                                                                    >
                                                                        Edit Report
                                                                    </Button>
                                                                </div>
                                                            )}

                                                        {canAdminEdit && (
                                                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
                                                                <p>
                                                                    You're editing this submitted report as a reviewer.
                                                                    Changes save back to the same investigation —
                                                                    checklist/visit gates don't apply, and the review
                                                                    SLA clock keeps running.
                                                                </p>
                                                                {investigation.reportLastEditedAt && (
                                                                    <p className="text-xs text-blue-700 mt-1">
                                                                        Last edited{' '}
                                                                        {new Date(
                                                                            investigation.reportLastEditedAt,
                                                                        ).toLocaleString(undefined, {
                                                                            day: '2-digit',
                                                                            month: 'short',
                                                                            year: 'numeric',
                                                                            hour: '2-digit',
                                                                            minute: '2-digit',
                                                                        })}
                                                                        {investigation.reportLastEditedBy
                                                                            ? ' by a reviewer.'
                                                                            : '.'}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Checklist gate */}
                                                        {canEdit && investigationChecklist && !isChecklistComplete && (
                                                            <div className="bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                                                                <div className="min-w-0 text-sm text-[#4e4d4d]">
                                                                    <p className="font-semibold text-[#1b1b1b] mb-0.5">
                                                                        Finish the investigator checklist first
                                                                    </p>
                                                                    <p className="text-xs">
                                                                        {investigationChecklist.answeredItems} of{' '}
                                                                        {investigationChecklist.totalItems} items
                                                                        answered. You can edit the report here, but it
                                                                        can only be saved after the checklist is
                                                                        complete.
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setReportSubTab('checklist')}
                                                                    className="shrink-0 bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-2 rounded-lg text-xs font-medium"
                                                                >
                                                                    Go to checklist
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Address visits gate */}
                                                        {canEdit && !hasAtLeastOneVisit && (
                                                            <div className="bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                                                                <div className="min-w-0 text-sm text-[#4e4d4d]">
                                                                    <p className="font-semibold text-[#1b1b1b] mb-0.5">
                                                                        Log at least one address visit
                                                                    </p>
                                                                    <p className="text-xs">
                                                                        The report can only be saved once you've
                                                                        recorded a real-world visit (home, workplace,
                                                                        reference, etc.) on the Visits tab.
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setReportSubTab('visits')}
                                                                    className="shrink-0 bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-2 rounded-lg text-xs font-medium"
                                                                >
                                                                    Go to visits
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Milestones gate */}
                                                        {canEdit && hasMilestones && !allMilestonesSubmitted && (
                                                            <div className="bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                                                                <div className="min-w-0 text-sm text-[#4e4d4d]">
                                                                    <p className="font-semibold text-[#1b1b1b] mb-0.5">
                                                                        Submit every milestone first
                                                                    </p>
                                                                    <p className="text-xs">
                                                                        {milestonesData?.submittedMilestones ?? 0} of{' '}
                                                                        {milestonesData?.totalMilestones ?? 0} sections
                                                                        submitted. The final report unlocks once every
                                                                        milestone is locked in.
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setReportSubTab('milestones')}
                                                                    className="shrink-0 bg-[#151515] hover:bg-[#2a2a2a] text-white px-4 py-2 rounded-lg text-xs font-medium"
                                                                >
                                                                    Go to milestones
                                                                </button>
                                                            </div>
                                                        )}

                                                        {/* Report Title */}
                                                        <div className="flex flex-col gap-2">
                                                            <label className="text-base font-semibold text-[#181d27]">
                                                                Report Title
                                                            </label>
                                                            {canEdit ? (
                                                                <input
                                                                    type="text"
                                                                    value={reportTitle}
                                                                    onChange={(e) => {
                                                                        setReportTitle(e.target.value.slice(0, 500));
                                                                        setReportSaveError('');
                                                                        setReportSaveSuccess('');
                                                                    }}
                                                                    maxLength={500}
                                                                    placeholder="Title Of Report"
                                                                    className="w-full bg-white border border-[#d5d7da] rounded-lg h-12 px-4 text-sm text-[#181d27] placeholder:text-[#717680] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors"
                                                                />
                                                            ) : (
                                                                <div className="w-full bg-[#fafafa] border border-[#e9eaeb] rounded-lg px-4 py-3 text-sm text-[#181d27]">
                                                                    {investigation.reportTitle || (
                                                                        <span className="italic text-[#717680]">
                                                                            No report title submitted yet.
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Executive Summary */}
                                                        <div className="flex flex-col gap-2">
                                                            <label className="text-base font-semibold text-[#181d27]">
                                                                Executive Summary
                                                            </label>
                                                            {canEdit ? (
                                                                <textarea
                                                                    value={reportExecutiveSummary}
                                                                    onChange={(e) => {
                                                                        setReportExecutiveSummary(e.target.value);
                                                                        setReportSaveError('');
                                                                        setReportSaveSuccess('');
                                                                    }}
                                                                    rows={8}
                                                                    placeholder="A short summary that appears at the top of the employer report (one or two paragraphs)."
                                                                    className="w-full bg-white border border-[#d5d7da] rounded-lg px-4 py-3 text-sm text-[#181d27] placeholder:text-[#717680] outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 transition-colors resize-y min-h-[200px]"
                                                                />
                                                            ) : (
                                                                <div className="w-full bg-[#fafafa] border border-[#e9eaeb] rounded-lg px-4 py-3 text-sm text-[#181d27] whitespace-pre-wrap min-h-[80px] leading-relaxed">
                                                                    {investigation.executiveSummary || (
                                                                        <span className="italic text-[#717680]">
                                                                            No executive summary submitted yet.
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Report Details — compiled automatically from the agent's
                                submitted milestones (one numbered section per milestone). Only
                                super-admins edit the compiled text directly. */}
                                                        <div className="flex flex-col gap-2">
                                                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                                                <label className="text-base font-semibold text-[#181d27]">
                                                                    Detailed Report
                                                                </label>
                                                                <p className="text-xs text-[#717680]">
                                                                    {canAdminEdit
                                                                        ? 'Edit the compiled findings before sharing with the employer.'
                                                                        : 'Compiled automatically from your submitted milestones.'}
                                                                </p>
                                                            </div>
                                                            {(() => {
                                                                // Agent view: show a read-only preview built from the
                                                                // milestones (the single source of truth). Admin edit:
                                                                // keep the section editor against the stored report.
                                                                const previewSections = canAdminEdit
                                                                    ? reportSections
                                                                    : buildReportSectionsFromMilestones(
                                                                          milestonesData?.milestones,
                                                                      );
                                                                if (canAdminEdit) {
                                                                    return (
                                                                        <ReportSectionEditor
                                                                            sections={reportSections}
                                                                            onChange={(next) => {
                                                                                setReportSections(next);
                                                                                setReportSaveError('');
                                                                                setReportSaveSuccess('');
                                                                                setReportSectionsError(null);
                                                                            }}
                                                                            highlightIndex={
                                                                                reportSectionsError?.index ?? null
                                                                            }
                                                                        />
                                                                    );
                                                                }
                                                                if (isAgent && previewSections.length > 0) {
                                                                    return (
                                                                        <>
                                                                            <div className="bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-2.5 text-xs text-[#4e4d4d]">
                                                                                These findings come straight from your
                                                                                milestones. Edit a milestone to change
                                                                                what appears here.
                                                                            </div>
                                                                            <div className="flex flex-col gap-3">
                                                                                {previewSections.map(
                                                                                    (section, index) => (
                                                                                        <div
                                                                                            key={index}
                                                                                            className="bg-[#fafafa] border border-[#e9eaeb] rounded-lg px-4 py-3"
                                                                                        >
                                                                                            {section.heading && (
                                                                                                <p className="text-sm font-semibold text-[#181d27] mb-1">
                                                                                                    {index + 1}.{' '}
                                                                                                    {section.heading}
                                                                                                </p>
                                                                                            )}
                                                                                            <p className="text-sm text-[#181d27] whitespace-pre-wrap leading-relaxed">
                                                                                                {section.body || (
                                                                                                    <span className="italic text-[#717680]">
                                                                                                        No notes
                                                                                                        recorded for
                                                                                                        this milestone.
                                                                                                    </span>
                                                                                                )}
                                                                                            </p>
                                                                                        </div>
                                                                                    ),
                                                                                )}
                                                                            </div>
                                                                        </>
                                                                    );
                                                                }
                                                                if (isAgent) {
                                                                    return (
                                                                        <div className="w-full bg-[#fafafa] border border-[#e9eaeb] rounded-lg px-4 py-4 text-sm min-h-[120px] leading-relaxed">
                                                                            <span className="italic text-[#717680]">
                                                                                Submit your milestones to compile the
                                                                                detailed report.
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                                return reportSections.length > 0 ? (
                                                                    <div className="flex flex-col gap-3">
                                                                        {reportSections.map((section, index) => (
                                                                            <div
                                                                                key={index}
                                                                                className="bg-[#fafafa] border border-[#e9eaeb] rounded-lg px-4 py-3"
                                                                            >
                                                                                {section.heading && (
                                                                                    <p className="text-sm font-semibold text-[#181d27] mb-1">
                                                                                        {index + 1}. {section.heading}
                                                                                    </p>
                                                                                )}
                                                                                <p className="text-sm text-[#181d27] whitespace-pre-wrap leading-relaxed">
                                                                                    {section.body}
                                                                                </p>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-full bg-[#fafafa] border border-[#e9eaeb] rounded-lg px-4 py-4 text-sm min-h-[120px] leading-relaxed">
                                                                        <span className="italic text-[#717680]">
                                                                            No report details submitted yet.
                                                                        </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {/* Social media report upload — only for plans whose
                                                            scope includes a social-media review. Uploads are
                                                            agent-only (server-enforced), so the control shows
                                                            for an agent working the case; everyone else sees
                                                            the uploaded reports read-only. */}
                                                        {investigation.consentSocialMediaReview && id && (
                                                            <SocialMediaReportPanel
                                                                investigationId={id}
                                                                canEdit={
                                                                    isAgent &&
                                                                    investigation.status ===
                                                                        InvestigationStatus.IN_PROGRESS
                                                                }
                                                            />
                                                        )}

                                                        {/* Inline save feedback */}
                                                        {reportSaveError && (
                                                            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                                                                {reportSaveError}
                                                            </div>
                                                        )}
                                                        {reportSaveSuccess && (
                                                            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700 flex items-center gap-2">
                                                                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                                                                {reportSaveSuccess}
                                                            </div>
                                                        )}

                                                        {/* Save Report */}
                                                        {canEdit && (
                                                            <div className="flex flex-col items-center pt-2 gap-1">
                                                                <div className="flex items-center gap-2">
                                                                    {canAdminEdit && (
                                                                        <Button
                                                                            type="button"
                                                                            variant="outline"
                                                                            onClick={() => {
                                                                                setAdminEditMode(false);
                                                                                // Reset edits by re-hydrating from the
                                                                                // server copy so a cancelled edit
                                                                                // doesn't leak into the next session.
                                                                                setReportTitle(
                                                                                    investigation.reportTitle ?? '',
                                                                                );
                                                                                setReportExecutiveSummary(
                                                                                    investigation.executiveSummary ??
                                                                                        '',
                                                                                );
                                                                                setReportSections(
                                                                                    parseReportSections(
                                                                                        investigation.reportDetails ??
                                                                                            investigation.investigationNotes ??
                                                                                            '',
                                                                                    ),
                                                                                );
                                                                                setReportSaveError('');
                                                                                setReportSectionsError(null);
                                                                            }}
                                                                            className="border-[#d5d7da] text-[#1b1b1b] hover:bg-gray-50 px-5 py-2.5 rounded-lg text-sm font-medium"
                                                                        >
                                                                            Cancel
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        onClick={handleSaveFieldReport}
                                                                        disabled={
                                                                            isSavingReport ||
                                                                            isUpdatingAdminReport ||
                                                                            (!canAdminEdit && !canSubmitReport)
                                                                        }
                                                                        className="bg-[#151515] hover:bg-[#2a2a2a] text-white px-6 py-2.5 rounded-lg text-sm font-medium min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    >
                                                                        {isSavingReport || isUpdatingAdminReport
                                                                            ? 'Saving...'
                                                                            : canAdminEdit
                                                                              ? 'Save Changes'
                                                                              : 'Save Report'}
                                                                    </Button>
                                                                </div>
                                                                {!canAdminEdit && !canSubmitReport && (
                                                                    <p className="text-xs text-[#717680]">
                                                                        {(() => {
                                                                            // Enumerate the still-missing gates so
                                                                            // the agent sees every blocker at once
                                                                            // rather than fixing one and rediscovering
                                                                            // the next.
                                                                            const missing: string[] = [];
                                                                            if (!isChecklistComplete)
                                                                                missing.push(
                                                                                    'the checklist is complete',
                                                                                );
                                                                            if (!hasAtLeastOneVisit)
                                                                                missing.push(
                                                                                    'at least one address visit is logged',
                                                                                );
                                                                            if (!hasRiskAssessment)
                                                                                missing.push(
                                                                                    'the risk assessment is submitted',
                                                                                );
                                                                            if (
                                                                                hasMilestones &&
                                                                                !allMilestonesSubmitted
                                                                            )
                                                                                missing.push(
                                                                                    'every milestone is submitted',
                                                                                );
                                                                            if (missing.length === 0) return null;
                                                                            if (missing.length === 1) {
                                                                                return `Save unlocks after ${missing[0]}.`;
                                                                            }
                                                                            const last = missing[missing.length - 1];
                                                                            const rest = missing
                                                                                .slice(0, -1)
                                                                                .join(', ');
                                                                            return `Save unlocks after ${rest} and ${last}.`;
                                                                        })()}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}

                                    {/* Visits Content (Agent / Super Admin) */}
                                    {!isEmployer && reportSubTab === 'visits' && id && investigation && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            <AddressVisitsPanel
                                                investigationId={id}
                                                canEdit={
                                                    isAgent && investigation.status === InvestigationStatus.IN_PROGRESS
                                                }
                                                isReportSubmitted={
                                                    investigation.status === InvestigationStatus.UNDER_REVIEW ||
                                                    investigation.status === InvestigationStatus.COMPLETED
                                                }
                                                status={investigation.status}
                                            />
                                        </div>
                                    )}

                                    {/* Milestones Content (Agent / Super Admin) */}
                                    {!isEmployer && reportSubTab === 'milestones' && id && investigation && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            {missingMilestones.length > 0 && (
                                                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
                                                    <p className="font-semibold mb-1">
                                                        {missingMilestones.length} milestone
                                                        {missingMilestones.length === 1 ? '' : 's'} still pending
                                                    </p>
                                                    <ul className="list-disc list-inside text-xs space-y-0.5">
                                                        {missingMilestones.map((m) => (
                                                            <li key={m.sectionId}>{m.title}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            <MilestonesPanel
                                                investigationId={id}
                                                canEdit={
                                                    isAgent && investigation.status === InvestigationStatus.IN_PROGRESS
                                                }
                                                canManageSections={isSuperAdmin}
                                            />
                                        </div>
                                    )}

                                    {/* Checklist Content (Agent / Super Admin) */}
                                    {!isEmployer && reportSubTab === 'checklist' && id && investigation && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            <InvestigationChecklistPanel
                                                investigationId={id}
                                                canEdit={
                                                    isAgent && investigation.status === InvestigationStatus.IN_PROGRESS
                                                }
                                                missingItems={missingChecklistItems}
                                                onClearMissing={() => setMissingChecklistItems([])}
                                            />
                                        </div>
                                    )}

                                    {/* Guarantors Content (Agent / Super Admin) */}
                                    {!isEmployer && reportSubTab === 'guarantors' && id && investigation && (
                                        <div className="flex-1 overflow-y-auto min-h-0">
                                            <GuarantorsPanel
                                                investigationId={id}
                                                canManage={isAgent || isSuperAdmin}
                                                // Reassignment is Customer-Success-owned now (§6); agents
                                                // can still verify but no longer reassign. Admins retain it.
                                                canReassign={isSuperAdmin}
                                                guarantorTownWarning={investigation.guarantorTownWarning}
                                                employmentTown={investigation.employmentTown}
                                                showCallWindow={!isEmployer}
                                            />

                                            {/* Previous-employer references — Super Admin oversight. */}
                                            {isSuperAdmin && (
                                                <div className="mt-8 border-t border-[#eee] pt-6">
                                                    <EmployerReferencesPanel
                                                        investigationId={id}
                                                        canRetrigger={isSuperAdmin}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ============ CONVERSATIONS TAB (employer only) ============ */}
                            {activeTab === 'conversations' && isEmployer && id && (
                                <div className="flex-1 overflow-y-auto min-h-0">
                                    <EmployerConversationsPanel
                                        investigationId={id}
                                        startWithCompose={conversationsStartCompose}
                                        canCompose={!!investigation.supportCode}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Agent Assignment Modal */}
            {showAssignModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowAssignModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-4">
                            {investigation?.agentId ? 'Reassign Agent' : 'Assign Agent'}
                        </h3>

                        <div className="mb-6">
                            <label className="block text-xs font-medium text-[#4e4d4d] mb-2">Select Agent</label>
                            <select
                                value={selectedAgentId}
                                onChange={(e) => setSelectedAgentId(e.target.value)}
                                className="w-full px-4 py-2.5 border border-[#e0e0e0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
                            >
                                <option value="">Choose an agent...</option>
                                {availableAgents.map((agent) => (
                                    <option key={agent.id} value={agent.id}>
                                        {agent.firstName} {agent.lastName}{' '}
                                        {agent.id === investigation?.agentId && '(Current)'}
                                    </option>
                                ))}
                            </select>
                            {availableAgents.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">No active agents available</p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setShowAssignModal(false);
                                    setSelectedAgentId('');
                                }}
                                variant="outline"
                                className="flex-1"
                                disabled={isAssigning || isReassigning}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAssignAgent}
                                className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                                disabled={!selectedAgentId || isAssigning || isReassigning}
                            >
                                {isAssigning || isReassigning
                                    ? 'Assigning...'
                                    : investigation?.agentId
                                      ? 'Reassign'
                                      : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Customer Success Assignment Modal */}
            {showAssignCsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowAssignCsModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-4">
                            {investigation?.customerSuccessId ? 'Reassign Customer Success' : 'Assign Customer Success'}
                        </h3>

                        <div className="mb-6">
                            <label className="block text-xs font-medium text-[#4e4d4d] mb-2">
                                Select Customer Success
                            </label>
                            <select
                                value={selectedCsId}
                                onChange={(e) => setSelectedCsId(e.target.value)}
                                className="w-full px-4 py-2.5 border border-[#e0e0e0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
                            >
                                <option value="">Choose a CS member...</option>
                                {availableCs.map((cs) => (
                                    <option key={cs.id} value={cs.id}>
                                        {cs.firstName} {cs.lastName}{' '}
                                        {cs.id === investigation?.customerSuccessId && '(Current)'}
                                    </option>
                                ))}
                            </select>
                            {availableCs.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">No Customer Success users available</p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setShowAssignCsModal(false);
                                    setSelectedCsId('');
                                }}
                                variant="outline"
                                className="flex-1"
                                disabled={isAssigningCs}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAssignCs}
                                className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                                disabled={!selectedCsId || isAssigningCs}
                            >
                                {isAssigningCs
                                    ? 'Assigning...'
                                    : investigation?.customerSuccessId
                                      ? 'Reassign'
                                      : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Medical User Assignment Modal */}
            {showAssignMedicalModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowAssignMedicalModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-4">
                            {investigation?.medicalUserId ? 'Reassign Medical User' : 'Assign Medical User'}
                        </h3>

                        <div className="mb-6">
                            <label className="block text-xs font-medium text-[#4e4d4d] mb-2">Select Medical User</label>
                            <select
                                value={selectedMedicalId}
                                onChange={(e) => setSelectedMedicalId(e.target.value)}
                                className="w-full px-4 py-2.5 border border-[#e0e0e0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
                            >
                                <option value="">Choose a medical user...</option>
                                {availableMedical.map((med) => (
                                    <option key={med.id} value={med.id}>
                                        {med.firstName} {med.lastName}{' '}
                                        {med.id === investigation?.medicalUserId && '(Current)'}
                                    </option>
                                ))}
                            </select>
                            {availableMedical.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">No medical users available</p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setShowAssignMedicalModal(false);
                                    setSelectedMedicalId('');
                                }}
                                variant="outline"
                                className="flex-1"
                                disabled={isAssigningMedical}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAssignMedical}
                                className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                                disabled={!selectedMedicalId || isAssigningMedical}
                            >
                                {isAssigningMedical
                                    ? 'Assigning...'
                                    : investigation?.medicalUserId
                                      ? 'Reassign'
                                      : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notary Assignment Modal */}
            {showAssignNotaryModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowAssignNotaryModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-4">
                            {investigation?.notaryUser ? 'Reassign Notary' : 'Assign Notary'}
                        </h3>

                        <div className="mb-6">
                            <label className="block text-xs font-medium text-[#4e4d4d] mb-2">Select Notary</label>
                            <select
                                value={selectedNotaryId}
                                onChange={(e) => setSelectedNotaryId(e.target.value)}
                                className="w-full px-4 py-2.5 border border-[#e0e0e0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
                            >
                                <option value="">Choose a notary...</option>
                                {availableNotaries.map((notary) => (
                                    <option key={notary.id} value={notary.id}>
                                        {notary.firstName} {notary.lastName}{' '}
                                        {notary.id === investigation?.notaryUser?.id && '(Current)'}
                                    </option>
                                ))}
                            </select>
                            {availableNotaries.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">
                                    No notaries available. Invite one from the Users page first.
                                </p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setShowAssignNotaryModal(false);
                                    setSelectedNotaryId('');
                                }}
                                variant="outline"
                                className="flex-1"
                                disabled={isAssigningNotary}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAssignNotary}
                                className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                                disabled={!selectedNotaryId || isAssigningNotary}
                            >
                                {isAssigningNotary ? 'Assigning...' : investigation?.notaryUser ? 'Reassign' : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Police Assignment Modal */}
            {showAssignPoliceModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowAssignPoliceModal(false)}
                    />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
                        <h3 className="text-lg font-bold text-[#1b1b1b] mb-4">
                            {investigation?.policeUser ? 'Reassign Police' : 'Assign Police'}
                        </h3>

                        <div className="mb-6">
                            <label className="block text-xs font-medium text-[#4e4d4d] mb-2">Select Police User</label>
                            <select
                                value={selectedPoliceId}
                                onChange={(e) => setSelectedPoliceId(e.target.value)}
                                className="w-full px-4 py-2.5 border border-[#e0e0e0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
                            >
                                <option value="">Choose a police user...</option>
                                {availablePolice.map((police) => (
                                    <option key={police.id} value={police.id}>
                                        {police.firstName} {police.lastName}{' '}
                                        {police.id === investigation?.policeUser?.id && '(Current)'}
                                    </option>
                                ))}
                            </select>
                            {availablePolice.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">
                                    No police users available. Invite one from the Users page first.
                                </p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button
                                onClick={() => {
                                    setShowAssignPoliceModal(false);
                                    setSelectedPoliceId('');
                                }}
                                variant="outline"
                                className="flex-1"
                                disabled={isAssigningPolice}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleAssignPolice}
                                className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                                disabled={!selectedPoliceId || isAssigningPolice}
                            >
                                {isAssigningPolice ? 'Assigning...' : investigation?.policeUser ? 'Reassign' : 'Assign'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Validation Modal */}
            <ValidationModal
                isOpen={showValidationModal}
                onClose={() => setShowValidationModal(false)}
                onSubmit={handleValidationSubmit}
                isLoading={isValidating}
                investigationId={investigation?.id || ''}
                employeeName={employeeName}
            />

            {/* Review Investigation Modal */}
            {showReviewModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowReviewModal(false)}
                    />
                    <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between p-5 pb-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                    <CheckCircle2 className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">Review Investigation</h2>
                                    <p className="text-xs text-gray-500">Approve or reject with feedback</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowReviewModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <XCircle className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-5">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                                <p className="text-xs text-amber-800">
                                    <span className="font-semibold">{employeeName}</span> — Status:{' '}
                                    <span className="font-semibold">Under Review</span>
                                </p>
                            </div>

                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Review Notes <span className="text-xs text-gray-400">(required for rejection)</span>
                            </label>
                            <textarea
                                value={validationNotes}
                                onChange={(e) => setValidationNotes(e.target.value)}
                                rows={4}
                                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                                placeholder="Add notes about your review decision..."
                            />
                        </div>

                        <div className="flex gap-3 p-5 pt-0">
                            <Button
                                onClick={async () => {
                                    await handleValidationSubmit(true, validationNotes || undefined);
                                    setValidationNotes('');
                                    setShowReviewModal(false);
                                }}
                                disabled={isValidating}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <CheckCircle2 className="w-4 h-4" />
                                {isValidating ? 'Processing...' : 'Approve'}
                            </Button>
                            <Button
                                onClick={async () => {
                                    if (!validationNotes?.trim()) {
                                        alert('Please provide a reason for rejection.');
                                        return;
                                    }
                                    await handleValidationSubmit(false, validationNotes);
                                    setValidationNotes('');
                                    setShowReviewModal(false);
                                }}
                                disabled={isValidating}
                                variant="outline"
                                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <XCircle className="w-4 h-4" />
                                {isValidating ? 'Processing...' : 'Reject'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Employer Details Modal */}
            {showEmployerModal && investigation?.employer && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowEmployerModal(false)}
                    />
                    <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between p-5 border-b border-gray-100">
                            <h2 className="text-lg font-bold text-gray-900">Employer Details</h2>
                            <button
                                onClick={() => setShowEmployerModal(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <XCircle className="w-4 h-4 text-gray-500" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">
                                    Company Name
                                </p>
                                <p className="text-sm font-medium text-[#1b1b1b]">
                                    {investigation.employer.companyName || '---'}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">
                                    Contact Person
                                </p>
                                <p className="text-sm font-medium text-[#1b1b1b]">
                                    {investigation.employer.firstName} {investigation.employer.lastName}
                                </p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">Email</p>
                                <p className="text-sm font-medium text-[#1b1b1b]">{investigation.employer.email}</p>
                            </div>
                            {investigation.employer.phoneNumber && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">Phone</p>
                                    <p className="text-sm font-medium text-[#1b1b1b]">
                                        {investigation.employer.phoneNumber}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className="p-5 pt-0">
                            <Button
                                onClick={() => setShowEmployerModal(false)}
                                variant="outline"
                                className="w-full py-2.5 rounded-lg text-sm"
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirmation (Super Admin only) — phrase + OTP gated */}
            {showDeleteModal && isSuperAdmin && investigation?.id && (
                <DeleteInvestigationModal
                    isOpen
                    investigationId={investigation.id}
                    employeeName={employeeName}
                    onClose={() => setShowDeleteModal(false)}
                    onDeleted={() => {
                        setShowDeleteModal(false);
                        navigate('/investigations');
                    }}
                />
            )}

            {/* Request missing info (Super Admin / Customer Success) */}
            {showRequestInfoModal && canRequestInfo && investigation?.id && (
                <RequestInfoModal
                    isOpen
                    investigationId={investigation.id}
                    employeeName={employeeName}
                    onClose={() => setShowRequestInfoModal(false)}
                />
            )}

            {/* Nudge employee / employer (Super Admin / Customer Success) */}
            {showNudgeModal && canRequestInfo && investigation?.id && (
                <NudgeModal
                    isOpen
                    investigationId={investigation.id}
                    employeeName={employeeName}
                    onClose={() => setShowNudgeModal(false)}
                    onSent={(msg) => setNudgeToast(msg)}
                />
            )}

            {/* Retrigger consent confirmation (Super Admin, pre-submission only) */}
            {showRetriggerModal && isSuperAdmin && investigation?.id && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                        onClick={() => !isRetriggering && setShowRetriggerModal(false)}
                    />
                    <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
                        <div className="p-5">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                                    <RefreshCw className="w-5 h-5 text-[#FF6B35]" />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-lg font-bold text-gray-900">Retrigger consent form</h2>
                                    <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                                        This invalidates <span className="font-medium">{employeeName}</span>'s current
                                        link and emails a new one. Any saved progress on the old link is discarded and
                                        the reminder clock restarts. This cannot be undone.
                                    </p>
                                </div>
                            </div>

                            {retriggerError && (
                                <p className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                                    {retriggerError}
                                </p>
                            )}

                            <div className="mt-6 flex items-center gap-3">
                                <Button
                                    onClick={() => setShowRetriggerModal(false)}
                                    disabled={isRetriggering}
                                    className="flex-1 py-2.5 rounded-lg text-sm bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleRetriggerConsent}
                                    disabled={isRetriggering}
                                    className="flex-1 py-2.5 rounded-lg text-sm bg-[#FF6B35] hover:bg-[#e0581f] text-white flex items-center justify-center gap-2"
                                >
                                    {isRetriggering ? 'Sending...' : 'Retrigger'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Nudge result toast */}
            {nudgeToast && (
                <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
                    <div className="flex items-center gap-3 rounded-xl bg-gray-900 px-4 py-3 text-sm text-white shadow-lg">
                        <BellRing className="h-4 w-4 text-[#FF6B35]" />
                        <span>{nudgeToast}</span>
                        <button
                            type="button"
                            onClick={() => setNudgeToast('')}
                            className="ml-2 text-white/70 hover:text-white"
                        >
                            <XCircle className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}
        </main>
    );
}
